{********************************************************************}
{                                                                    }
{ written by TMS Software                                            }
{            copyright (c) 2018 - 2021                               }
{            Email : info@tmssoftware.com                            }
{            Web : https://www.tmssoftware.com                       }
{                                                                    }
{ The source code is given as is. The author is not responsible      }
{ for any possible damage done due to the use of this code.          }
{ The complete source code remains property of the author and may    }
{ not be distributed, published, given or sold in any form as such.  }
{ No parts of the source code can be included in any other component }
{ or application without written authorization of the author.        }
{********************************************************************}

{$I WEBLib.TMSFNCDefines.inc}

unit WEBLib.TMSFNCWidgetGauge;

interface

uses
  Classes, WEBLib.TMSFNCCustomControl, WEBLib.TMSFNCGraphics,
  WEBLib.TMSFNCTypes, WEBLib.TMSFNCGraphicsTypes, WEBLib.TMSFNCBitmapContainer,
  WEBLib.TMSFNCWidgetGraphicsTypes, WEBLib.TMSFNCUtils,
  WEBLib.TMSFNCGraphicUtils
 {$IFDEF FMXLIB}
  ,FMX.Types, WEBLib.Controls
  {$ENDIF}
  {$IFNDEF LCLLIB}
  , Types
  {$ENDIF}
  {$IFDEF VCLLIB}
  ,Windows, Messages, ExtCtrls
  {$ENDIF}
  {$IFDEF LCLLIB}
  ,ExtCtrls
  {$ENDIF}
  {$IFNDEF WEBLIB}
  {$IFNDEF LCLLIB}
  ,UITypes
  {$ENDIF}
  {$ENDIF}
  {$IFDEF FMXLIB}
  , WEBLib.Graphics
  {$else}
  {$IFNDEF WEBLIB}
  , Graphics
  {$ELSE}
  , WEBLib.Graphics, WEBLib.ExtCtrls
  {$ENDIF}
  {$ENDIF}
  ;

const
  MAJ_VER = 1; // Major version nr.
  MIN_VER = 0; // Minor version nr.
  REL_VER = 1; // Release nr.
  BLD_VER = 1; // Build nr.

  // version history
  // v1.0.0.0 : First Release
  // v1.0.0.1 : Fixed: Issue with drawing digits in Android/iOS
  // v1.0.1.0 : New: Added GaugeValuesFormat and ValuePosition properties
  // v1.0.1.1 : Fixed : Issue in Delphi 11 with begin and end scene for CreateBitmapCanvas

type
  TCalibrationValueType = (cvNone, cvSmall, cvLarge);

  TCalibrationValue = record
    x, y, x1, y1, tx, ty: Double;
    ValueType: TCalibrationValueType;
    rv: Double;
  end;

  TTMSFNCWidgetGaugeCustom = class;

  TTMSFNCWidgetGaugeThresholdKind = (tkAngle, tkValue);

  TTMSFNCWidgetGaugeValuePosition = (vpTop, vpBottom);

  TTMSFNCWidgetGaugeThreshold = class(TPersistent)
  private
    FOwner: TTMSFNCWidgetGaugeCustom;
    FOnChanged: TNotifyEvent;

    FColor: TTMSFNCGraphicsColor;
    FStartAngle: integer;
    FSweepAngle: integer;
    FCenter: Double;
    FSpan: integer;
    FStartValue: Double;
    FEndValue: Double;
    FThresHoldKind: TTMSFNCWidgetGaugeThresholdKind;
  protected
    procedure DoOnChanged;

    procedure DrawingPropertiesChanged(Sender: TObject);
    procedure SetCenter(const Value: Double);
    procedure SetColor(const Value: TTMSFNCGraphicsColor);
    procedure SetSpan(const Value: integer);
    procedure SetStartAngle(const Value: integer);
    procedure SetSweepAngle(const Value: integer);
    procedure SetEndValue(const Value: Double);
    procedure SetStartValue(const Value: Double);
    procedure SetThresHoldKind(const Value: TTMSFNCWidgetGaugeThresholdKind);
    function GetStartAngle: integer;
    function GetSweepAngle: integer;
  public
    constructor Create(AOwner: TTMSFNCWidgetGaugeCustom);
    procedure Assign(Source: TPersistent); override;
  published
    property Color: TTMSFNCGraphicsColor read FColor write SetColor default gcPowderBlue;
    property Center: Double read FCenter write SetCenter;
    property Span: integer read FSpan write SetSpan default 25;
    property OnChanged: TNotifyEvent read FOnChanged write FOnChanged;
    property StartAngle: integer read GetStartAngle write SetStartAngle default 135;
    property SweepAngle: integer read GetSweepAngle write SetSweepAngle default 270;
    property StartValue: Double read FStartValue write SetStartValue;
    property EndValue: Double read FEndValue write SetEndValue;
    property ThresholdKind: TTMSFNCWidgetGaugeThresholdKind read FThresHoldKind write SetThresHoldKind default tkAngle;
  end;

  TTMSFNCWidgetGaugeArc = class(TPersistent)
  private
    FOwner: TTMSFNCWidgetGaugeCustom;
    FOnChanged: TNotifyEvent;

    FColor: TTMSFNCGraphicsColor;
    FWidth: Double;
    FStartAngle: integer;
    FStopAngle: integer;
    FThreshold: TTMSFNCWidgetGaugeThreshold;
  protected
    procedure DoOnChanged;

    procedure DrawingPropertiesChanged(Sender: TObject);
    procedure SetColor(const Value: TTMSFNCGraphicsColor);
    procedure SetStartAngle(const Value: integer);
    procedure SetStopAngle(const Value: integer);
    procedure SetThreshold(const Value: TTMSFNCWidgetGaugeThreshold);
    procedure SetWidth(const Value: Double);
    function GetStartAngle: integer;
    function GetStopAngle: integer;
  public
    constructor Create(AOwner: TTMSFNCWidgetGaugeCustom);
    destructor Destroy; override;
    procedure Assign(Source: TPersistent); override;
  published
    property Color: TTMSFNCGraphicsColor read FColor write SetColor default gcLightcyan;
    property Width: Double read FWidth write SetWidth;
    property StartAngle: integer read GetStartAngle write SetStartAngle default 135;
    property StopAngle: integer read GetStopAngle write SetStopAngle default 405;
    property Threshold: TTMSFNCWidgetGaugeThreshold read FThreshold write SetThreshold;
    property OnChanged: TNotifyEvent read FOnChanged write FOnChanged;
  end;

  TTMSFNCWidgetGaugeOuterCircle = class(TPersistent)
  private
    FOnChanged: TNotifyEvent;

    FColor: TTMSFNCGraphicsColor;
    FWidth: Double;
  protected
    procedure DoOnChanged;

    procedure DrawingPropertiesChanged(Sender: TObject);
    procedure SetColor(const Value: TTMSFNCGraphicsColor);
    procedure SetWidth(const Value: Double);
  public
    constructor Create;
    procedure Assign(Source: TPersistent); override;
  published
    property Color: TTMSFNCGraphicsColor read FColor write SetColor default gcPowderBlue;
    property Width: Double read FWidth write SetWidth;
    property OnChanged: TNotifyEvent read FOnChanged write FOnChanged;
  end;

  TTMSFNCWidgetGaugeOuterRim = class(TPersistent)
  private
    FOnChanged: TNotifyEvent;

    FColor: TTMSFNCGraphicsColor;
    FWidth: Integer;
  protected
    procedure DoOnChanged;

    procedure DrawingPropertiesChanged(Sender: TObject);
    procedure SetColor(const Value: TTMSFNCGraphicsColor);
    procedure SetWidth(const Value: integer);
  public
    constructor Create;
    procedure Assign(Source: TPersistent); override;
  published
    property Color: TTMSFNCGraphicsColor read FColor write SetColor default gcDarkGrey;
    property Width: integer read FWidth write SetWidth;
    property OnChanged: TNotifyEvent read FOnChanged write FOnChanged;
  end;

  TTMSFNCWidgetGaugeInnerCircle = class(TPersistent)
  private
    FOnChanged: TNotifyEvent;

    FColor: TTMSFNCGraphicsColor;
  protected
    procedure DoOnChanged;

    procedure DrawingPropertiesChanged(Sender: TObject);
    procedure SetColor(const Value: TTMSFNCGraphicsColor);
  public
    constructor Create;
    destructor Destroy; override;
    procedure Assign(Source: TPersistent); override;
  published
    property Color: TTMSFNCGraphicsColor read FColor write SetColor default gcBlue;
    property OnChanged: TNotifyEvent read FOnChanged write FOnChanged;
  end;

  TTMSFNCWidgetGaugeDigit = class(TPersistent)
  private
    fColor: TTMSFNCGraphicsColor;
    fBackgroundColor: TTMSFNCGraphicsColor;
    FOnChanged: TNotifyEvent;
    FVisible: Boolean;
    FAlignment: TAlignment;
  protected
    procedure DoOnChanged;

    procedure DrawingPropertiesChanged(Sender: TObject);
    procedure SetBackGroundColor(const Value: TTMSFNCGraphicsColor);
    procedure SetColor(const Value: TTMSFNCGraphicsColor);
    procedure SetVisible(const Value: Boolean);
    procedure SetAlignment(const Value: TAlignment);
  public
    constructor Create;
    procedure Assign(Source: TPersistent); override;
  published
    property Color: TTMSFNCGraphicsColor read fColor write SetColor default gcNavy;
    property BackGroundColor: TTMSFNCGraphicsColor read fBackgroundColor write SetBackGroundColor default gcPowderBlue;
    property Visible: Boolean read FVisible write SetVisible default true;
    property Alignment: TAlignment read FAlignment write SetAlignment default taCenter;
    property OnChanged: TNotifyEvent read FOnChanged write FOnChanged;
  end;

  TTMSFNCWidgetGaugeNeedle = class(TPersistent)
  private
    FOnChanged: TNotifyEvent;

    FColor: TTMSFNCGraphicsColor;
    FShineColor: TTMSFNCGraphicsColor;
    FShineColorTo: TTMSFNCGraphicsColor;
    FOuterCenterColor: TTMSFNCGraphicsColor;
    FOuterCenterColorTo: TTMSFNCGraphicsColor;
    FInnerCenterColor: TTMSFNCGraphicsColor;
    FInnerCenterColorTo: TTMSFNCGraphicsColor;
  protected
    procedure DoOnChanged;

    procedure DrawingPropertiesChanged(Sender: TObject);
    procedure SetColor(const Value: TTMSFNCGraphicsColor);
    procedure SetInnerCenterColor(const Value: TTMSFNCGraphicsColor);
    procedure SetInnerCenterColorTo(const Value: TTMSFNCGraphicsColor);
    procedure SetOuterCenterColor(const Value: TTMSFNCGraphicsColor);
    procedure SetOuterCenterColorTo(const Value: TTMSFNCGraphicsColor);
    procedure SetShineColor(const Value: TTMSFNCGraphicsColor);
    procedure SetShineColorTo(const Value: TTMSFNCGraphicsColor);
  public
    constructor Create;
    procedure Assign(Source: TPersistent); override;
  published
    property Color: TTMSFNCGraphicsColor read FColor write SetColor default gcNavy;
    property ShineColor: TTMSFNCGraphicsColor read FShineColor write SetShineColor default gcAzure;
    property ShineColorTo: TTMSFNCGraphicsColor read FShineColorTo write SetShineColorTo default gcLightgray;
    property OuterCenterColor: TTMSFNCGraphicsColor read FOuterCenterColor write SetOuterCenterColor default gcPowderBlue;
    property OuterCenterColorTo: TTMSFNCGraphicsColor read FOuterCenterColorTo write SetOuterCenterColorTo default gcNavy;
    property InnerCenterColor: TTMSFNCGraphicsColor read FInnerCenterColor write SetInnerCenterColor default gcLightcyan;
    property InnerCenterColorTo: TTMSFNCGraphicsColor read FInnerCenterColorTo write SetInnerCenterColorTo default gcPowderBlue;
    property OnChanged: TNotifyEvent read FOnChanged write FOnChanged;
  end;

  TTMSFNCWidgetGaugeNeedleItem = class(TCollectionItem)
  private
    FValue: Double;
    FColor: TTMSFNCGraphicsColor;
    FShineColor: TTMSFNCGraphicsColor;
    FShineColorTo: TTMSFNCGraphicsColor;
  protected
    procedure DoOnChanged;

    procedure DrawingPropertiesChanged(Sender: TObject);
    procedure SetValue(const Value: Double);
    procedure SetColor(const Value: TTMSFNCGraphicsColor);
    procedure SetShineColor(const Value: TTMSFNCGraphicsColor);
    procedure SetShineColorTo(const Value: TTMSFNCGraphicsColor);
  public
{$ifndef LCL}
    constructor Create(ACollection: TCollection); override;
{$else}
    constructor Create(ACollection: TCollection); override;
{$endif}
    destructor Destroy; override;
    procedure Assign(Source: TPersistent); override;
  published
    property Color: TTMSFNCGraphicsColor read FColor write SetColor default gcNavy;
    property ShineColor: TTMSFNCGraphicsColor read FShineColor write SetShineColor default gcAzure;
    property ShineColorTo: TTMSFNCGraphicsColor read FShineColorTo write SetShineColorTo default gcLightgray;
    property Value: Double read FValue write SetValue;
  end;

  { TOwnedCollection is meant for ObjectInspector editing
    when you click on Items... type of property. It forces
    to have an owner (don't pass nil to constructor). If
    you want to call owner from any methods (we didn't need
    that), you can cast owner to the parent class type.
  }
  TTMSFNCWidgetGaugeNeedles = class(TOwnedCollection)
  private
    FOnChanged: TNotifyEvent;

    function GetItem(Index: Integer): TTMSFNCWidgetGaugeNeedleItem;
    procedure SetItem(Index: Integer; const Value: TTMSFNCWidgetGaugeNeedleItem);
  protected
    procedure Update(Item: TCollectionItem); override;
    procedure DoOnChanged;
  public
    constructor Create(AOwner: TTMSFNCWidgetGaugeCustom);
    function Add: TTMSFNCWidgetGaugeNeedleItem;
    function Insert(Index: Integer): TTMSFNCWidgetGaugeNeedleItem;
    property Items[Index: Integer]: TTMSFNCWidgetGaugeNeedleItem read GetItem write SetItem; default;
    procedure Delete(Index: Integer);
  published
    property OnChanged: TNotifyEvent read FOnChanged write FOnChanged;
  end;

  TTMSFNCWidgetGaugeSectionType = (stCustomMargin, stBorder);

  TTMSFNCWidgetGaugeSection = class(TCollectionItem)
  private
    FColor: TTMSFNCGraphicsColor;
    FStartValue: Double;
    FEndValue: Double;
    FInnerMargin: integer;
    FOuterMargin: integer;
    FSectionType: TTMSFNCWidgetGaugeSectionType;
  protected
    procedure DoOnChanged;

    procedure DrawingPropertiesChanged(Sender: TObject);
    procedure SetColor(const Value: TTMSFNCGraphicsColor);
    procedure SetEndValue(const Value: Double);
    procedure SetStartValue(const Value: Double);
    procedure SetInnerMargin(const Value: integer);
    procedure SetOuterMargin(const Value: integer);
    procedure SetSectionType(const Value: TTMSFNCWidgetGaugeSectionType);
  public
{$ifndef LCL}
    constructor Create(ACollection: TCollection); override;
{$else}
    constructor Create(ACollection: TCollection); override;
{$endif}
    destructor Destroy; override;
    procedure Assign(Source: TPersistent); override;
  published
    property Color: TTMSFNCGraphicsColor read FColor write SetColor default gcBlue;
    property StartValue: Double read FStartValue write SetStartValue;
    property EndValue: Double read FEndValue write SetEndValue;
    property OuterMargin: integer read FOuterMargin write SetOuterMargin default 20;
    property InnerMargin: integer read FInnerMargin write SetInnerMargin default 0;
    property SectionType: TTMSFNCWidgetGaugeSectionType read FSectionType write SetSectionType default stCustomMargin;
  end;

  { TOwnedCollection is meant for ObjectInspector editing
    when you click on Items... type of property. It forces
    to have an owner (don't pass nil to constructor). If
    you want to call owner from any methods (we didn't need
    that), you can cast owner to the parent class type.
  }
  TTMSFNCWidgetGaugeSections = class(TOwnedCollection)
  private
    FOnChanged: TNotifyEvent;

    function GetItem(Index: Integer): TTMSFNCWidgetGaugeSection;
    procedure SetItem(Index: Integer; const Value: TTMSFNCWidgetGaugeSection);
  protected
    procedure Update(Item: TCollectionItem); override;
    procedure DoOnChanged;
  public
    constructor Create(AOwner: TTMSFNCWidgetGaugeCustom);
    function Add: TTMSFNCWidgetGaugeSection;
    function Insert(Index: Integer): TTMSFNCWidgetGaugeSection;
    property Items[Index: Integer]: TTMSFNCWidgetGaugeSection read GetItem write SetItem; default;
    procedure Delete(Index: Integer);
  published
    property OnChanged: TNotifyEvent read FOnChanged write FOnChanged;
  end;

  TTMSFNCWidgetGaugeValueChanged = procedure(Sender: TObject; value: Double) of object;

  TTMSFNCWidgetGaugeDisplayValue = procedure(Sender: TObject; var value: Double) of object;

  TTMSFNCWidgetGaugeGetValueText = procedure(Sender: TObject; value: Double; var str: String) of object;

  TTMSFNCWidgetGaugeCustom = class(TTMSFNCCustomControl)
  private
    FDesignTime: Boolean;
    FBorder: TTMSFNCWidgetGraphicsStroke;

    doAnimation: Boolean;
    FSta, FSwpa: double;
    // This is the rectangle of the whole gauge (inside margin)
    frectImg: TRectF;
    // This is the rectangle of the Arc inside
    Frg: TRectF;

    FCalibrationValues: array[0..1000] of TCalibrationValue;
    FanimationTimer: TTimer;
    fminValue: Double;
    fmaxValue: Double;
    fcurrentValue: Double;
    FcurrentValueTo: Double;
    fcurrentDisplayValue: Double;
    FcurrentDisplayValueTo: Double;
    FAnimCurrentValue: Double;
    FAnimCurrentDisplayValue: Double;
    FValueFormat: string;
    fnoOfDivisions: Integer;
    fnoOfSubDivisions: Integer;
    fdialText: string;
    FMarginOuter: Integer;

    fFont: TTMSFNCGraphicsFont;
    FValueFont: TTMSFNCGraphicsFont;
    FDigit: TTMSFNCWidgetGaugeDigit;
    FOuterCircle: TTMSFNCWidgetGaugeOuterCircle;
    FInnerCircle: TTMSFNCWidgetGaugeInnerCircle;
    FOuterRim: TTMSFNCWidgetGaugeOuterRim;
    FNeedle: TTMSFNCWidgetGaugeNeedle;
    FArc: TTMSFNCWidgetGaugeArc;
    FSubDivWidth: integer;
    FDivWidth: integer;
    fSubDivColor: TTMSFNCGraphicsColor;
    fDivColor: TTMSFNCGraphicsColor;
    FAnimation: Boolean;
    FOnValueChanged: TTMSFNCWidgetGaugeValueChanged;
    FOnChange: TNotifyEvent;
    FOnDisplayValue: TTMSFNCWidgetGaugeDisplayValue;
    FOnGetValueText: TTMSFNCWidgetGaugeGetValueText;

    FAnimationFactor: integer;
    FSections: TTMSFNCWidgetGaugeSections;
    FShowValues: Boolean;
    FCircleStartValue: Integer;
    FCircleEndValue: Integer;
    FExtraNeedles: TTMSFNCWidgetGaugeNeedles;
    FLogaritchmic: Boolean;
    FLogaritchmicBase: Integer;
    FUnlimitedValue: Boolean;
    FUnlimitedDisplayValue: Boolean;
    FOnDisplayValueChanged: TTMSFNCWidgetGaugeValueChanged;
    FGaugeValuesFormat: string;
    FValuePosition: TTMSFNCWidgetGaugeValuePosition;
    procedure SetDialText(const Value: string);
    procedure SetMaxValue(const Value: Double);
    procedure SetMinValue(const Value: double);
    procedure SetNoOfDivisions(const Value: Integer);
    procedure SetNoOfSubDivisions(const Value: Integer);
    procedure SetValue(const Value: Double);
    procedure SetFont(const Value: TTMSFNCGraphicsFont);
    procedure SetValueFont(const Value: TTMSFNCGraphicsFont);
    procedure SetValueFormat(const Value: string);
    procedure SetDigit(const Value: TTMSFNCWidgetGaugeDigit);
    procedure SetInnerCircle(const Value: TTMSFNCWidgetGaugeInnerCircle);
    procedure SetOuterCircle(const Value: TTMSFNCWidgetGaugeOuterCircle);
    procedure SetOuterRim(const Value: TTMSFNCWidgetGaugeOuterRim);
    procedure SetNeedle(const Value: TTMSFNCWidgetGaugeNeedle);
    procedure SetArc(const Value: TTMSFNCWidgetGaugeArc);
    procedure SetDivColor(const Value: TTMSFNCGraphicsColor);
    procedure SetDivWidth(const Value: integer);
    procedure SetSubDivWidth(const Value: integer);
    procedure SetSubDivColor(const Value: TTMSFNCGraphicsColor);
    procedure SetAnimation(const Value: Boolean);
    procedure SetAnimationFactor(const Value: integer);
    procedure SetSections(const Value: TTMSFNCWidgetGaugeSections);
    procedure SetShowValues(const Value: Boolean);
    procedure SetCircleEndValue(const Value: Integer);
    procedure SetCircleStartValue(const Value: Integer);
    procedure SetExtraNeedles(const Value: TTMSFNCWidgetGaugeNeedles);
    procedure SetLogarithmic(const Value: Boolean);
    procedure SetLogarithmicBase(const Value: Integer);
    function GetValue: Double;
    procedure SetUnlimitedValue(const Value: Boolean);
    procedure SetUnlimitedDisplayValue(const Value: Boolean);
    function GetDisplayValue: Double;
    procedure SetBorder(const Value: TTMSFNCWidgetGraphicsStroke);
    procedure SetMarginOuter(const Value: Integer);
    procedure SetGaugeValuesFormat(const Value: string);
    procedure SetValuePosition(
      const Value: TTMSFNCWidgetGaugeValuePosition);
  protected
    procedure DoValueChanged(Sender: TObject; value: Double);
    procedure DoDisplayValueChanged(Sender: TObject; value: Double);
    function GetGaugeValueTextSize(AValue: Double; AWidth: Boolean): Integer;
    procedure DrawSections(AGraphics: TTMSFNCGraphics);
    procedure DrawCalibration(AGraphics: TTMSFNCGraphics);
    procedure DrawString(AGraphics: TTMSFNCGraphics; value: string; ft: TTMSFNCGraphicsFont; pt: TPointF; DialDraw: Boolean);
    procedure DisplayNumber(AGraphics: TTMSFNCGraphics; number: Double; drect: TRectF);
    procedure DrawDigit(AGraphics: TTMSFNCGraphics; number: Integer; position: TPointF; dp, t: Boolean; h: integer);
    procedure DrawPointer(AGraphics: TTMSFNCGraphics; col, shncol, shncolto: TTMSFNCGraphicsColor; cx: Double; cy: Double; value: Double);
    procedure DrawCenterPoint(AGraphics: TTMSFNCGraphics; rect: TRectF; cX: Integer; cY: Integer);
    procedure CalculateDrawingParameters(ARect: TRectF);
    procedure CalculateImageRect(ARect: TRectF);
    procedure CalculateCalibration;
    procedure CalculateArcThresHold;
    function XYToScale(X, Y: integer): Double;
    function GetRadian(theta: Double): Double;
    function GetX(x: Double; awidth: Double): Double;
    function GetY(y: Double; aheight: Double): Double;
    function IsNumberAvailable(Number: integer; const listOfNumbers: array of integer): Boolean;
    function GetAngle(Value: Double): Double;
    function CalcPosition(Value: Double): TTMSFNCGraphicsPathPolygon;
    function PtInPoly(const Points: TTMSFNCGraphicsPathPolygon; X,Y: Integer): Boolean;
    procedure AnimateGauge(Sender: TObject);

    procedure DrawingPropertiesChanged(Sender: TObject);
    procedure Draw({%H-}AGraphics: TTMSFNCGraphics; {%H-}ARect: TRectF); override;

    function GetDocURL: string; override;
    function GetTipsURL: string; override;

    property Border: TTMSFNCWidgetGraphicsStroke read FBorder write SetBorder;
    property UnlimitedValue: Boolean read FUnlimitedValue write SetUnlimitedValue;
    property UnlimitedDisplayValue: Boolean read FUnlimitedDisplayValue write SetUnlimitedDisplayValue;
    property AnimationFactor: integer read FAnimationFactor write SetAnimationFactor;
    property MinimumValue: Double read fminvalue write SetMinValue;
    property MaximumValue: Double read fmaxvalue write SetMaxValue;
    property Value: Double read GetValue write SetValue;
    property Digit: TTMSFNCWidgetGaugeDigit read FDigit write SetDigit;
    property OuterCircle: TTMSFNCWidgetGaugeOuterCircle read FOuterCircle write SetOuterCircle;
    property InnerCircle: TTMSFNCWidgetGaugeInnerCircle read FInnerCircle write SetInnerCircle;
    property OuterRim: TTMSFNCWidgetGaugeOuterRim read FOuterRim write SetOuterRim;
    property Needle: TTMSFNCWidgetGaugeNeedle read FNeedle write SetNeedle;
    property Arc: TTMSFNCWidgetGaugeArc read FArc write SetArc;
    property DivisionCount: Integer read fnoOfDivisions write SetNoOfDivisions;
    property SubDivisionCount: Integer read fnoOfSubDivisions write SetNoOfSubDivisions;
    property DivisionColor: TTMSFNCGraphicsColor read fDivColor write SetDivColor;
    property SubDivisionColor: TTMSFNCGraphicsColor read fSubDivColor write SetSubDivColor;
    property DivisionWidth: integer read FDivWidth write SetDivWidth;
    property SubDivisionWidth: integer read FSubDivWidth write SetSubDivWidth;
    property DialText: string read fdialText write SetDialText;
    property Font: TTMSFNCGraphicsFont read fFont write SetFont;
    property GaugeValuesFormat: string read FGaugeValuesFormat write SetGaugeValuesFormat;
    property ValueFont: TTMSFNCGraphicsFont read FValueFont write SetValueFont;
    property ValueFormat: string read FValueFormat write SetValueFormat;
    property ShowValues: Boolean read FShowValues write SetShowValues;
    property Animation: Boolean read FAnimation write SetAnimation;
    property Sections: TTMSFNCWidgetGaugeSections read FSections write SetSections;
    property CircleStartValue: Integer read FCircleStartValue write SetCircleStartValue;
    property CircleEndValue: Integer read FCircleEndValue write SetCircleEndValue;
    property Logarithmic: Boolean read FLogaritchmic write SetLogarithmic;
    property LogarithmicBase: Integer read FLogaritchmicBase write SetLogarithmicBase;
    property MarginOuter: integer read FMarginOuter write SetMarginOuter;
    property ValuePosition: TTMSFNCWidgetGaugeValuePosition read FValuePosition write SetValuePosition;

    property OnValueChanged: TTMSFNCWidgetGaugeValueChanged read FOnValueChanged write FOnValueChanged;
    property OnDisplayValueChanged: TTMSFNCWidgetGaugeValueChanged read FOnDisplayValueChanged write FOnDisplayValueChanged;
    property OnDisplayValue: TTMSFNCWidgetGaugeDisplayValue read FOnDisplayValue write FOnDisplayValue;
    property OnGetValueText: TTMSFNCWidgetGaugeGetValueText read FOnGetValueText write FOnGetValueText;
    property OnChange: TNotifyEvent read FOnChange write FOnChange;
    property ExtraNeedles: TTMSFNCWidgetGaugeNeedles read FExtraNeedles write SetExtraNeedles;
  public
    constructor Create(AOwner: TComponent); override;
    destructor Destroy; override;
    procedure Assign(Source: TPersistent); override;
    procedure Loaded; override;
    property DisplayValue: Double read GetDisplayValue;
  end;

  {$IFNDEF LCLLIB}
  [ComponentPlatformsAttribute(TMSPlatformsWeb)]
  {$ENDIF}
  TTMSFNCWidgetGauge = class(TTMSFNCWidgetGaugeCustom)
  protected
    function GetVersion: string; override;
  public
    constructor Create(AOwner: TComponent); override;
    destructor Destroy; override;
    procedure Assign(Source: TPersistent); override;
    procedure RegisterRuntimeClasses; override;
  published
    property Version: string read GetVersion;
    property Fill;
    property MarginOuter default 10;
    property Border;
    property UnlimitedValue default false;
    property UnlimitedDisplayValue default false;
    property AnimationFactor default 5;
    property MinimumValue;
    property MaximumValue;
    property Value;
    property Digit;
    property OuterCircle;
    property InnerCircle;
    property OuterRim;
    property Needle;
    property Arc;
    property DivisionCount default 10;
    property SubDivisionCount default 3;
    property DivisionColor default gcNavy;
    property SubDivisionColor default gcNavy;
    property DivisionWidth default 5;
    property SubDivisionWidth default 2;
    property DialText;
    property Font;
    property GaugeValuesFormat;
    property ValueFont;
    property ValueFormat;
    property ShowValues default true;
    property Animation default true;
    property Sections;
    property CircleStartValue default 0;
    property CircleEndValue default 360;
    property Logarithmic default False;
    property LogarithmicBase default 10;
    property ValuePosition default vpBottom;

    property OnValueChanged;
    property OnDisplayValueChanged;
    property OnDisplayValue;
    property OnGetValueText;
    property OnChange;
    property ExtraNeedles;
  end;

implementation

uses SysUtils, Math;

{ TTMSFNCWidgetGaugeDigit }

procedure TTMSFNCWidgetGaugeDigit.Assign(Source: TPersistent);
begin
  if (Source is TTMSFNCWidgetGaugeDigit) then
  begin
    FColor := (Source as TTMSFNCWidgetGaugeDigit).Color;
    FBackGroundColor := (Source as TTMSFNCWidgetGaugeDigit).BackGroundColor;
    FVisible := (Source as TTMSFNCWidgetGaugeDigit).Visible;
    FAlignment := (Source as TTMSFNCWidgetGaugeDigit).Alignment;
  end
  else
    inherited;
end;

procedure TTMSFNCWidgetGaugeDigit.DoOnChanged;
begin
  if Assigned(fOnChanged) then
    fOnChanged(Self);
end;

constructor TTMSFNCWidgetGaugeDigit.Create;
begin
  fColor := gcNavy;
  fBackgroundColor := gcPowderBlue;
  FVisible := true;
  FAlignment := taCenter;
end;

procedure TTMSFNCWidgetGaugeDigit.SetAlignment(const Value: TAlignment);
begin
  if FAlignment <> value then
  begin
    FAlignment := Value;
    DoOnChanged;
  end;
end;

procedure TTMSFNCWidgetGaugeDigit.DrawingPropertiesChanged(Sender: TObject);
begin
  DoOnChanged;
end;

procedure TTMSFNCWidgetGaugeDigit.SetBackGroundColor(const Value: TTMSFNCGraphicsColor);
begin
  if FBackGroundColor <> Value then
  begin
    fBackgroundColor := Value;
    DoOnChanged;
  end;
end;

procedure TTMSFNCWidgetGaugeDigit.SetColor(const Value: TTMSFNCGraphicsColor);
begin
  if fColor <> value then
  begin
    fColor := Value;
    DoOnChanged;
  end;
end;

procedure TTMSFNCWidgetGaugeDigit.SetVisible(const Value: Boolean);
begin
  if FVisible <> value then
  begin
    FVisible := Value;
    DoOnChanged;
  end;
end;

{ TTMSFNCWidgetGaugeOuterCircle }

procedure TTMSFNCWidgetGaugeOuterCircle.Assign(Source: TPersistent);
begin
  if (Source is TTMSFNCWidgetGaugeOuterCircle) then
  begin
    FColor := (Source as TTMSFNCWidgetGaugeOuterCircle).Color;
    FWidth := (Source as TTMSFNCWidgetGaugeOuterCircle).Width;
  end
  else
    inherited;
end;

procedure TTMSFNCWidgetGaugeOuterCircle.DoOnChanged;
begin
  if Assigned(FOnChanged) then
    FOnChanged(Self);
end;

constructor TTMSFNCWidgetGaugeOuterCircle.Create;
begin
  FColor := gcPowderBlue;
  FWidth := 0.03;
end;

procedure TTMSFNCWidgetGaugeOuterCircle.DrawingPropertiesChanged(Sender: TObject);
begin
  DoOnChanged;
end;

procedure TTMSFNCWidgetGaugeOuterCircle.SetColor(const Value: TTMSFNCGraphicsColor);
begin
  if FColor <> Value then
  begin
    FColor := Value;
    DoOnChanged;
  end;
end;

procedure TTMSFNCWidgetGaugeOuterCircle.SetWidth(const Value: Double);
begin
  if FWidth <> Value then
  begin
    FWidth := Value;
    DoOnChanged;
  end;
end;

{ TTMSFNCWidgetGaugeInnerCircle }

procedure TTMSFNCWidgetGaugeInnerCircle.Assign(Source: TPersistent);
begin
  if (Source is TTMSFNCWidgetGaugeInnerCircle) then
  begin
    Color := (Source as TTMSFNCWidgetGaugeInnerCircle).Color;
  end
  else
    inherited;
end;

procedure TTMSFNCWidgetGaugeInnerCircle.DoOnChanged;
begin
  if Assigned(FOnChanged) then
    FOnChanged(Self);
end;

constructor TTMSFNCWidgetGaugeInnerCircle.Create;
begin
  FColor := gcPowderBlue;
end;


destructor TTMSFNCWidgetGaugeInnerCircle.Destroy;
begin
  inherited;
end;

procedure TTMSFNCWidgetGaugeInnerCircle.SetColor(const Value: TTMSFNCGraphicsColor);
begin
  if FColor <> Value then
  begin
    FColor := Value;
    DoOnChanged;
  end;
end;

procedure TTMSFNCWidgetGaugeInnerCircle.DrawingPropertiesChanged(Sender: TObject);
begin
  DoOnChanged;
end;

{ TTMSFNCWidgetGaugeOuterRim }

procedure TTMSFNCWidgetGaugeOuterRim.Assign(Source: TPersistent);
begin
  if (Source is TTMSFNCWidgetGaugeOuterRim) then
  begin
    FColor := (Source as TTMSFNCWidgetGaugeOuterRim).Color;
    FWidth := (Source as TTMSFNCWidgetGaugeOuterRim).Width;
  end
  else
    inherited;
end;

procedure TTMSFNCWidgetGaugeOuterRim.DoOnChanged;
begin
  if Assigned(FOnChanged) then
    FOnChanged(Self);
end;

constructor TTMSFNCWidgetGaugeOuterRim.Create;
begin
  FColor := gcSlategray;
  FWidth := 2;
end;

procedure TTMSFNCWidgetGaugeOuterRim.DrawingPropertiesChanged(Sender: TObject);
begin
  DoOnChanged;
end;

procedure TTMSFNCWidgetGaugeOuterRim.SetColor(const Value: TTMSFNCGraphicsColor);
begin
  if FColor <> value then
  begin
    FColor := Value;
    DoOnChanged;
  end;
end;

procedure TTMSFNCWidgetGaugeOuterRim.SetWidth(const Value: integer);
begin
  if FWidth <> value then
  begin
    FWidth := Value;
    DoOnChanged;
  end;
end;

{ TTMSFNCWidgetGaugeNeedle }

procedure TTMSFNCWidgetGaugeNeedle.Assign(Source: TPersistent);
begin
  if (Source is TTMSFNCWidgetGaugeNeedle) then
  begin
    FColor := (Source as TTMSFNCWidgetGaugeNeedle).Color;
    FShineColor := (Source as TTMSFNCWidgetGaugeNeedle).ShineColor;
    FShineColorTo := (Source as TTMSFNCWidgetGaugeNeedle).ShineColorTo;
    FOuterCenterColor := (Source as TTMSFNCWidgetGaugeNeedle).OuterCenterColor;
    FOuterCenterColorTo := (Source as TTMSFNCWidgetGaugeNeedle).FOuterCenterColorTo;
    FInnerCenterColor := (Source as TTMSFNCWidgetGaugeNeedle).InnerCenterColor;
    FInnerCenterColorTo := (Source as TTMSFNCWidgetGaugeNeedle).InnerCenterColorTo;
  end
  else
    inherited;
end;

procedure TTMSFNCWidgetGaugeNeedle.DoOnChanged;
begin
  if Assigned(FOnChanged) then
    FOnChanged(Self);
end;

constructor TTMSFNCWidgetGaugeNeedle.Create;
begin
  FColor :=gcNavy;
  OuterCenterColor := gcPowderBlue;
  OuterCenterColorTo := gcNavy;
  InnerCenterColor := gcLightcyan;
  InnerCenterColorTo := gcPowderBlue;
  ShineColor := gcAzure;
  ShineColorTo := gcLightgray;
end;

procedure TTMSFNCWidgetGaugeNeedle.DrawingPropertiesChanged(Sender: TObject);
begin
  DoOnChanged;
end;

procedure TTMSFNCWidgetGaugeNeedle.SetColor(const Value: TTMSFNCGraphicsColor);
begin
  if FColor <> Value then
  begin
    FColor := Value;
    DoOnChanged;
  end;
end;

procedure TTMSFNCWidgetGaugeNeedle.SetInnerCenterColor(const Value: TTMSFNCGraphicsColor);
begin
  if FInnerCenterColor <> value then
  begin
    FInnerCenterColor := Value;
    DoOnChanged;
  end;
end;

procedure TTMSFNCWidgetGaugeNeedle.SetInnerCenterColorTo(const Value: TTMSFNCGraphicsColor);
begin
  if FInnerCenterColorTo <> Value then
  begin
    FInnerCenterColorTo := Value;
    DoOnChanged;
  end;
end;

procedure TTMSFNCWidgetGaugeNeedle.SetOuterCenterColor(const Value: TTMSFNCGraphicsColor);
begin
  if FOuterCenterColor <> value then
  begin
    FOuterCenterColor := Value;
    DoOnChanged;
  end;
end;

procedure TTMSFNCWidgetGaugeNeedle.SetOuterCenterColorTo(const Value: TTMSFNCGraphicsColor);
begin
  if FouterCenterColorTo <> value then
  begin
    FOuterCenterColorTo := Value;
    DoOnChanged;
  end;
end;

procedure TTMSFNCWidgetGaugeNeedle.SetShineColor(const Value: TTMSFNCGraphicsColor);
begin
  if FShineColor <> value then
  begin
    FShineColor := Value;
    DoOnChanged;
  end;
end;

procedure TTMSFNCWidgetGaugeNeedle.SetShineColorTo(const Value: TTMSFNCGraphicsColor);
begin
  if FShineColorTo <> value then
  begin
    FShineColorTo := Value;
    DoOnChanged;
  end;
end;

{ TTMSFNCWidgetGaugeThreshold }

procedure TTMSFNCWidgetGaugeThreshold.Assign(Source: TPersistent);
begin
  if (Source is TTMSFNCWidgetGaugeThreshold) then
  begin
    FColor := (Source as TTMSFNCWidgetGaugeThreshold).Color;
    FStartAngle := (Source as TTMSFNCWidgetGaugeThreshold).StartAngle;
    FSweepAngle := (Source as TTMSFNCWidgetGaugeThreshold).SweepAngle;
    FCenter := (Source as TTMSFNCWidgetGaugeThreshold).Center;
    FSpan := (Source as TTMSFNCWidgetGaugeThreshold).Span;
    FStartValue := (Source as TTMSFNCWidgetGaugeThreshold).StartValue;
    FEndValue := (Source as TTMSFNCWidgetGaugeThreshold).EndValue;
    FThresHoldKind := (Source as TTMSFNCWidgetGaugeThreshold).ThresholdKind;
  end
  else
    inherited;
end;

procedure TTMSFNCWidgetGaugeThreshold.DoOnChanged;
begin
  if Assigned(FOnChanged) then
    FOnChanged(Self);
end;

constructor TTMSFNCWidgetGaugeThreshold.Create(AOwner: TTMSFNCWidgetGaugeCustom);
begin
  FOwner := AOwner;
  FColor := gcPowderBlue;
  FStartAngle := 135;
  FSweepAngle := 270;
  FCenter := 25;
  FSpan := 25;
  if (csDesigning in fOwner.ComponentState) then
  begin
    FStartValue := 0;
    FEndValue := 100;
  end;
  FThresHoldKind := tkAngle;
end;

function TTMSFNCWidgetGaugeThreshold.GetStartAngle: integer;
begin
  Result := Max(FStartAngle, FOwner.CircleStartValue);
end;

function TTMSFNCWidgetGaugeThreshold.GetSweepAngle: integer;
begin
  Result := Min(FSweepAngle, FOwner.CircleEndValue - FOwner.CircleStartValue);
end;

procedure TTMSFNCWidgetGaugeThreshold.DrawingPropertiesChanged(Sender: TObject);
begin
  DoOnChanged;
end;

procedure TTMSFNCWidgetGaugeThreshold.SetCenter(const Value: Double);
begin
  if ((value >= FOwner.fminValue) and (value <= FOwner.fmaxValue)) then
  begin
    fCenter := value;
    DoOnChanged;
  end;
end;

procedure TTMSFNCWidgetGaugeThreshold.SetColor(const Value: TTMSFNCGraphicsColor);
begin
  if FColor <> value then
  begin
    FColor := Value;
    DoOnChanged;
  end;
end;

procedure TTMSFNCWidgetGaugeThreshold.SetEndValue(const Value: Double);
begin
  if FEndValue <> Value then
  begin
    FEndValue := Value;
    DoOnChanged;
  end;
end;

procedure TTMSFNCWidgetGaugeThreshold.SetSpan(const Value: integer);
begin
  if ((value >= 0) and (value <= 100)) then
  begin
    fSpan := value;
    DoOnChanged;
  end;
end;

procedure TTMSFNCWidgetGaugeThreshold.SetStartAngle(const Value: integer);
begin
  if FStartAngle <> value then
  begin
    FStartAngle := Max(Value, FOwner.CircleStartValue);
    DoOnChanged;
  end;
end;

procedure TTMSFNCWidgetGaugeThreshold.SetStartValue(const Value: Double);
begin
  if FStartValue <> value then
  begin
    FStartValue := Value;
    DoOnChanged;
  end;
end;

procedure TTMSFNCWidgetGaugeThreshold.SetSweepAngle(const Value: integer);
begin
  if FSweepAngle <> value then
  begin
    FSweepAngle := Value;
    DoOnChanged;
  end;
end;

procedure TTMSFNCWidgetGaugeThreshold.SetThresHoldKind(
  const Value: TTMSFNCWidgetGaugeThresholdKind);
begin
  if FThresHoldKind <> value then
  begin
    FThresHoldKind := Value;
    DoOnChanged;
  end;
end;

{ TTMSFNCWidgetGaugeArc }

procedure TTMSFNCWidgetGaugeArc.Assign(Source: TPersistent);
begin
  if (Source is TTMSFNCWidgetGaugeArc) then
  begin
    FColor := (Source as TTMSFNCWidgetGaugeArc).Color;
    FWidth := (Source as TTMSFNCWidgetGaugeArc).Width;
    FStartAngle := (Source as TTMSFNCWidgetGaugeArc).StartAngle;
    FStopAngle := (Source as TTMSFNCWidgetGaugeArc).StopAngle;
    FThreshold.Assign((Source as TTMSFNCWidgetGaugeArc).Threshold);
  end
  else
    inherited;
end;

procedure TTMSFNCWidgetGaugeArc.DoOnChanged;
begin
  if Assigned(FOnChanged) then
    FOnChanged(Self);
end;

constructor TTMSFNCWidgetGaugeArc.Create(AOwner: TTMSFNCWidgetGaugeCustom);
begin
  FOwner := AOwner;
  FWidth := 0.03;
  FColor := gcLightcyan;
  FStartAngle := 135;
  FStopAngle := 405;
  FThreshold := TTMSFNCWidgetGaugeThreshold.Create(AOwner);
  FThreshold.OnChanged := @DrawingPropertiesChanged;
end;

destructor TTMSFNCWidgetGaugeArc.Destroy;
begin
  FThreshold.Free;
  inherited;
end;

function TTMSFNCWidgetGaugeArc.GetStartAngle: integer;
begin
  Result := Max(FStartAngle, FOwner.CircleStartValue);
end;

function TTMSFNCWidgetGaugeArc.GetStopAngle: integer;
begin
  if FStopAngle >= FOwner.CircleEndValue - FOwner.CircleStartValue + StartAngle then
    Result := FOwner.CircleEndValue  - FOwner.CircleStartValue + StartAngle
  else
    Result := FStopAngle;
end;

procedure TTMSFNCWidgetGaugeArc.SetColor(const Value: TTMSFNCGraphicsColor);
begin
  if FColor <> value then
  begin
    FColor := Value;
    DoOnChanged;
  end;
end;

procedure TTMSFNCWidgetGaugeArc.SetStartAngle(const Value: integer);
begin
  if FStartAngle <> value then
  begin
    FStartAngle := Max(Value, FOwner.CircleStartValue);
    DoOnChanged;
  end;
end;

procedure TTMSFNCWidgetGaugeArc.SetStopAngle(const Value: integer);
begin
  if FStopAngle <> value then
  begin
    FStopAngle := Value;
    DoOnChanged;
  end;
end;

procedure TTMSFNCWidgetGaugeArc.SetThreshold(const Value: TTMSFNCWidgetGaugeThreshold);
begin
  if FThreshold <> Value then
  begin
    FThreshold.Assign(Value);
    DoOnChanged;
  end;
end;

procedure TTMSFNCWidgetGaugeArc.SetWidth(const Value: Double);
begin
  if FWidth <> value then
  begin
    FWidth := Value;
    DoOnChanged;
  end;
end;

procedure TTMSFNCWidgetGaugeArc.DrawingPropertiesChanged(Sender: TObject);
begin
  DoOnChanged;
end;

{ TTMSFNCWidgetGaugeSection }

{$ifndef LCL}
constructor TTMSFNCWidgetGaugeSection.Create(ACollection: TCollection);
{$else}
constructor TTMSFNCWidgetGaugeSection.Create(ACollection: TCollection);
{$endif}
begin
  inherited;
  FColor := gcBlue;
  FStartValue := 0;
  FEndValue := 100;
  FOuterMargin := 20;
  FInnerMargin := 0;
  FSectionType := stCustomMargin;
  DoOnChanged;
end;

destructor TTMSFNCWidgetGaugeSection.Destroy;
begin
  inherited;
end;

procedure TTMSFNCWidgetGaugeSection.Assign(Source: TPersistent);
begin
  if (Source is TTMSFNCWidgetGaugeSection) then
  begin
    FColor := (Source as TTMSFNCWidgetGaugeSection).Color;
    FStartValue := (Source as TTMSFNCWidgetGaugeSection).StartValue;
    FEndValue := (Source as TTMSFNCWidgetGaugeSection).EndValue;
    FOuterMargin := (Source as TTMSFNCWidgetGaugeSection).OuterMargin;
    FInnerMargin := (Source as TTMSFNCWidgetGaugeSection).InnerMargin;
    FSectionType := (Source as TTMSFNCWidgetGaugeSection).SectionType;
  end
  else
    inherited Assign(Source);
end;

procedure TTMSFNCWidgetGaugeSection.DoOnChanged;
begin
  Changed(false); //as part of collection
end;

procedure TTMSFNCWidgetGaugeSection.DrawingPropertiesChanged(Sender: TObject);
begin
  DoOnChanged;
end;

procedure TTMSFNCWidgetGaugeSection.SetColor(const Value: TTMSFNCGraphicsColor);
begin
  if FColor <> value then
  begin
    FColor := Value;
    DoOnChanged;
  end;
end;

procedure TTMSFNCWidgetGaugeSection.SetEndValue(const Value: Double);
begin
  if FEndValue <> value then
  begin
    FEndValue := Value;
    DoOnChanged;
  end;
end;

procedure TTMSFNCWidgetGaugeSection.SetInnerMargin(const Value: integer);
begin
  if FInnerMargin <> value then
  begin
    FInnerMargin := Value;
    DoOnChanged;
  end;
end;

procedure TTMSFNCWidgetGaugeSection.SetOuterMargin(const Value: integer);
begin
  if FOuterMargin <> value then
  begin
    FOuterMargin := Value;
    DoOnChanged;
  end;
end;

procedure TTMSFNCWidgetGaugeSection.SetSectionType(
  const Value: TTMSFNCWidgetGaugeSectionType);
begin
  if FSectionType <> Value then
  begin
    FSectionType := Value;
    DoOnChanged;
  end;
end;

procedure TTMSFNCWidgetGaugeSection.SetStartValue(const Value: Double);
begin
  if FStartValue <> Value then
  begin
    FStartValue := Value;
    DoOnChanged;
  end;
end;

{ TTMSFNCWidgetGaugeSections }

function TTMSFNCWidgetGaugeSections.Add: TTMSFNCWidgetGaugeSection;
begin
  result := TTMSFNCWidgetGaugeSection(inherited Add);
end;

constructor TTMSFNCWidgetGaugeSections.Create(AOwner: TTMSFNCWidgetGaugeCustom);
begin
  inherited Create(AOwner, TTMSFNCWidgetGaugeSection);
end;

procedure TTMSFNCWidgetGaugeSections.Delete(Index: Integer);
begin
  inherited Delete(Index);
end;

function TTMSFNCWidgetGaugeSections.GetItem(
  Index: Integer): TTMSFNCWidgetGaugeSection;
begin
  Result := TTMSFNCWidgetGaugeSection(inherited Items[Index]);
end;

// Items inform us here of any changes by internal collection
// mechanism via changed(false) or true call.
procedure TTMSFNCWidgetGaugeSections.Update(Item: TCollectionItem);
begin
  inherited;
  DoOnChanged;
end;

procedure TTMSFNCWidgetGaugeSections.DoOnChanged;
begin
  if Assigned(fOnChanged) then
    fOnChanged(self);
end;

function TTMSFNCWidgetGaugeSections.Insert(Index: Integer): TTMSFNCWidgetGaugeSection;
begin
  Result := TTMSFNCWidgetGaugeSection(inherited Insert(Index));
end;

procedure TTMSFNCWidgetGaugeSections.SetItem(Index: Integer;
  const Value: TTMSFNCWidgetGaugeSection);
begin
  inherited Items[Index] := Value;
end;

{ TTMSFNCWidgetGaugeNeedles }

function TTMSFNCWidgetGaugeNeedles.Add: TTMSFNCWidgetGaugeNeedleItem;
begin
  result := TTMSFNCWidgetGaugeNeedleItem(inherited Add);
end;

constructor TTMSFNCWidgetGaugeNeedles.Create(AOwner: TTMSFNCWidgetGaugeCustom);
begin
  inherited Create(AOwner, TTMSFNCWidgetGaugeNeedleItem);
end;

procedure TTMSFNCWidgetGaugeNeedles.Delete(Index: Integer);
begin
  inherited Delete(Index);
end;

function TTMSFNCWidgetGaugeNeedles.GetItem(
  Index: Integer): TTMSFNCWidgetGaugeNeedleItem;
begin
  Result := TTMSFNCWidgetGaugeNeedleItem(inherited Items[Index]);
end;

function TTMSFNCWidgetGaugeNeedles.Insert(Index: Integer): TTMSFNCWidgetGaugeNeedleItem;
begin
  Result := TTMSFNCWidgetGaugeNeedleItem(inherited Insert(Index));
end;

procedure TTMSFNCWidgetGaugeNeedles.SetItem(Index: Integer;
  const Value: TTMSFNCWidgetGaugeNeedleItem);
begin
  inherited Items[Index] := Value;
end;

// Items inform us here of any changes by internal collection
// mechanism via changed(false) or true call.
procedure TTMSFNCWidgetGaugeNeedles.Update(Item: TCollectionItem);
begin
  inherited;
  DoOnChanged;
end;

procedure TTMSFNCWidgetGaugeNeedles.DoOnChanged;
begin
  if Assigned(fOnChanged) then
    fOnChanged(self);
end;


{ TTMSFNCWidgetGaugeNeedleItem }

{$ifndef LCL}
constructor TTMSFNCWidgetGaugeNeedleItem.Create(ACollection: TCollection);
{$else}
constructor TTMSFNCWidgetGaugeNeedleItem.Create(ACollection: TCollection);
{$endif}
begin
  inherited;
  FColor := gcNavy;
  FShineColor := gcAzure;
  FShineColorTo := gcLightgray;

  case Index of
    0: FColor := gcGreen;
    1: FColor := gcRed;
    2: FColor := gcYellow;
  else
  end;
end;

destructor TTMSFNCWidgetGaugeNeedleItem.Destroy;
begin
  inherited;
end;

procedure TTMSFNCWidgetGaugeNeedleItem.Assign(Source: TPersistent);
begin
  inherited Assign(Source);
  if Source is TTMSFNCWidgetGaugeNeedleItem then
  begin
    FColor := (Source as TTMSFNCWidgetGaugeNeedleItem).Color;
    FShineColor := (Source as TTMSFNCWidgetGaugeNeedleItem).ShineColor;
    FShineColorTo := (Source as TTMSFNCWidgetGaugeNeedleItem).ShineColorTo;
  end;
end;

procedure TTMSFNCWidgetGaugeNeedleItem.DoOnChanged;
begin
  Changed(false); //as part of collection
end;


procedure TTMSFNCWidgetGaugeNeedleItem.SetColor(const Value: TTMSFNCGraphicsColor);
begin
  if FColor <> Value then
  begin
    FColor := Value;
    DoOnChanged;
  end;
end;

procedure TTMSFNCWidgetGaugeNeedleItem.SetShineColor(const Value: TTMSFNCGraphicsColor);
begin
  if FShineColor <> Value then
  begin
    FShineColor := Value;
    DoOnChanged;
  end;
end;

procedure TTMSFNCWidgetGaugeNeedleItem.SetShineColorTo(const Value: TTMSFNCGraphicsColor);
begin
  if FShineColorTo <> Value then
  begin
    FShineColorTo := Value;
    DoOnChanged;
  end;
end;

procedure TTMSFNCWidgetGaugeNeedleItem.DrawingPropertiesChanged(Sender: TObject);
begin
  DoOnChanged;
end;

procedure TTMSFNCWidgetGaugeNeedleItem.SetValue(const Value: Double);
var
  v: Double;
begin
  v := Max(Min(value, (Collection.Owner as TTMSFNCWidgetGaugeCustom).MaximumValue),
      (Collection.Owner as TTMSFNCWidgetGaugeCustom).MinimumValue);
  FValue := v;
  DoOnChanged;
end;

{TTMSFNCWidgetGaugeCustom}

procedure TTMSFNCWidgetGaugeCustom.Assign(Source: TPersistent);
begin
  inherited Assign(Source);
  if (Source is TTMSFNCWidgetGaugeCustom) then
  begin
    FBorder.Assign((Source as TTMSFNCWidgetGaugeCustom).Border);
  end;
end;

constructor TTMSFNCWidgetGaugeCustom.Create(AOwner: TComponent);
begin
  inherited;

  {$IFDEF MSWINDOWS}
  NativeCanvas := true;
  {$ENDIF}
  TextQuality := gtqDefault;

  Width := 350;
  Height := 350;
  FMarginOuter := 10;
  FDesignTime := (csDesigning in ComponentState) and not
    ((csReading in Owner.ComponentState) or (csLoading in Owner.ComponentState));

  FBorder := TTMSFNCWidgetGraphicsStroke.Create;
  FBorder.Color := gcDimGray;
  FBorder.Width := 1;
  FBorder.OnChanged := @DrawingPropertiesChanged;

  if FDesignTime then
  begin
    Fill.Color := gcWhite;
    FIll.Kind := gfkSolid;
  end;

  FAnimation := True;
  FShowValues := true;
  FAnimationFactor := 5;

  FFont := TTMSFNCGraphicsFont.Create;
  FFont.Color := gcGray;
  FFont.Size := 12;
  FValueFont := TTMSFNCGraphicsFont.Create;
  FValueFont.Color := gcDarkslategray;
  FValueFont.Size := 12;
  FUnlimitedValue := False;
  FUnlimitedDisplayValue := False;
  FValuePosition := vpBottom;

  FValuefont.OnChanged := @DrawingPropertiesChanged;
  FFont.OnChanged := @DrawingPropertiesChanged;

  Width := 350;
  Height := 350;
  FDivWidth := 5;
  FSubDivWidth := 2;
  FDivColor := gcNavy;
  FSubDivColor := gcNavy;

  FCircleStartValue := 0;
  FCircleEndValue := 360;

  FValueFormat := '000.00';
  FGaugeValuesFormat := '0';

  //fxp := 5;
  //fyp := 5;
  fnoOfDivisions := 10;
  fnoOfSubDivisions := 3;
  fminValue := 0;
  fmaxValue := 100;
  fcurrentValue := 0;
  fcurrentDisplayValue := 0;
  FcurrentValueTo := 0;
  FcurrentDisplayValueTo := 0;
  FAnimCurrentValue := 0;
  FAnimCurrentDisplayValue := 0;

  FLogaritchmic := False;
  FLogaritchmicBase := 10;

  FDigit := TTMSFNCWidgetGaugeDigit.Create;
  FDigit.OnChanged := @DrawingPropertiesChanged;

  FOuterCircle := TTMSFNCWidgetGaugeOuterCircle.Create;
  FOuterCircle.OnChanged := @DrawingPropertiesChanged;

  FInnerCircle := TTMSFNCWidgetGaugeInnerCircle.Create;
  FInnerCircle.OnChanged := @DrawingPropertiesChanged;

  FOuterRim := TTMSFNCWidgetGaugeOuterRim.Create;
  FOuterRim.OnChanged := @DrawingPropertiesChanged;

  FNeedle := TTMSFNCWidgetGaugeNeedle.Create;
  FNeedle.OnChanged := @DrawingPropertiesChanged;

  FArc := TTMSFNCWidgetGaugeArc.Create(self);
  FArc.OnChanged := @DrawingPropertiesChanged;

  FSections := TTMSFNCWidgetGaugeSections.Create(self);
  FSections.OnChanged := @DrawingPropertiesChanged;

  FanimationTimer := TTimer.Create(self);
  FAnimationTimer.Interval := 1;
  FAnimationTimer.Enabled := true;
  FanimationTimer.OnTimer := @AnimateGauge;

  FExtraNeedles := TTMSFNCWidgetGaugeNeedles.Create(self);
  FExtraNeedles.OnChanged := @DrawingPropertiesChanged;

  FDesignTime := (csDesigning in ComponentState) and not
    ((csReading in Owner.ComponentState) or (csLoading in Owner.ComponentState));
end;

destructor TTMSFNCWidgetGaugeCustom.Destroy;
begin
  if FArc <> nil then
    FArc.Free;
  if FDigit <> nil then
    FDigit.Free;
  if FOuterCircle <> nil then
    FOuterCircle.Free;
  if FInnerCircle <> nil then
    FInnerCircle.Free;
  if FFont <> nil then
    FFont.Free;
  if FValueFont <> nil then
    FValueFont.Free;
  if FExtraNeedles <> nil then
    FExtraNeedles.Free;
  if FNeedle <> nil then
    FNeedle.Free;
  if FOuterRim <> nil then
    FOuterRim.Free;
  if FanimationTimer <> nil then
    FanimationTimer.Free;
  if FSections <> nil then
    FSections.Free;
  if FBorder <> nil then
    FBorder.Free;
  inherited;
end;

procedure TTMSFNCWidgetGaugeCustom.CalculateDrawingParameters(ARect: TRectF);
begin
  CalculateImageRect(ARect);
  CalculateCalibration;
  CalculateArcThresHold;
end;

procedure TTMSFNCWidgetGaugeCustom.CalculateImageRect(ARect: TRectF);
var
  LowerDim: Single;
begin
  frectImg := ARect;
  FNCUtilInflateRect(frectImg, -FMarginOuter, -FMarginOuter);
  LowerDim := min(GetRectWidth(frectImg), GetRectHeight(frectImg));
  frectImg.Left := ARect.Left + (GetRectWidth(ARect) - LowerDim) / 2.0;
  frectImg.Top := ARect.Top + (GetRectHeight(ARect) - LowerDim) / 2.0;
  frectImg.Right := frectImg.Left + LowerDim;
  frectImg.Bottom := frectImg.top + LowerDim;
end;


procedure TTMSFNCWidgetGaugeCustom.Draw(AGraphics: TTMSFNCGraphics;
  ARect: TRectF);
var
  RadiusPoint: TPointF;
  sa, swa: Single;
  drf, dr: TRectF;
  I: Integer;
begin
  AGraphics.Fill.Assign(Fill);
  AGraphics.Stroke.Assign(FBorder);
  AGraphics.DrawRectangle(ARect);

  CalculateDrawingParameters(Arect);

  RadiusPoint := PointF(GetRectWidth(frectImg) / 2, GetRectHeight(frectImg) / 2);

  // Draw Background circle //
  if InnerCircle.Color <> gcNull then
  begin
    AGraphics.Fill.Kind := gfkSolid;
    AGraphics.Fill.Color := InnerCircle.Color;
    AGraphics.Stroke.Kind := gskNone;

    //Special fix. If it is a full circle then only
    //the path drawing utility works fine. Otherwise,
    //draw a regular arc.
    if CircleEndValue - CircleStartValue = 360 then
      FNCUtilDrawFilledArc(AGraphics,
               GetCenterPoint(frectImg),
               RadiusPoint,
               CircleStartValue,
               CircleEndValue - CircleStartValue)
    else
      AGraphics.DrawArc(
               GetCenterPoint(frectImg),
               RadiusPoint,
               CircleStartValue,
               CircleEndValue - CircleStartValue);
  end;

  //Draw Outline circle on the inner side
  if OuterCircle.Color <> gcNull then
  begin
    AGraphics.Fill.Kind := gfkNone;
    AGraphics.Stroke.Color := OuterCircle.color;
    AGraphics.Stroke.Kind := gskSolid;
    AGraphics.Stroke.Width := GetRectWidth(frectImg)  * OuterCircle.Width;

    AGraphics.DrawArc(
        GetCenterPoint(frectImg),
        RadiusPoint,
        CircleStartValue, CircleEndValue - CircleStartValue);
  end;

  // Outer rim
  if OuterRim.Color <> gcNull then
  begin
    AGraphics.Fill.Kind := gfkNone;
    AGraphics.Stroke.Color := OuterRim.color;
    AGraphics.Stroke.Kind := gskSolid;
    AGraphics.Stroke.Width := OuterRim.Width;
    AGraphics.DrawArc(
        GetCenterPoint(frectImg),
        PointF(RadiusPoint.X - OuterRim.Width/2,
               RadiusPoint.Y - OuterRim.Width/2),
        CircleStartValue, CircleEndValue - CircleStartValue);
  end;

  DrawSections(AGraphics);

  DrawCalibration(AGraphics);

  // Draw Arc on circle //
  if Arc.Color <> gcNull then
  begin
    if Arc.Threshold.ThresholdKind = tkValue then
    begin
      sa := GetAngle(arc.Threshold.StartValue);
      swa := GetAngle(arc.Threshold.EndValue) - sa;
    end
    else
    begin
      sa := arc.Threshold.StartAngle;
      swa := arc.Threshold.SweepAngle;
    end;
    AGraphics.Fill.Kind := gfkNone;
    AGraphics.Stroke.Color := Arc.Color;
    AGraphics.Stroke.Kind := gskSolid;
    AGraphics.Stroke.Width := GetRectWidth(frectImg) * Arc.Width;

    AGraphics.DrawArc(
        GetCenterPoint(Frg),
        PointF(GetRectWidth(Frg) / 2,
               GetRectHeight(Frg) / 2),
               sa,
               swa);
  end;

  // Draw Arc Threshold //
  if arc.Threshold.Color <> gcNull then
  begin
    AGraphics.Stroke.Color := Arc.Threshold.Color;
    AGraphics.Stroke.Width := GetRectWidth(frectImg) * Arc.Width;
    AGraphics.Fill.Kind := gfkNone;
    AGraphics.DrawArc(
        GetCenterPoint(Frg),
        PointF(GetRectWidth(Frg) / 2,
               GetRectHeight(Frg) / 2),
               FSta,
               FSwpa);
  end;

  if Digit.Visible then
  begin
    // Draw Rectangle to display number //
    //if Digit.BackGroundColor <> gcNull then
    begin
        dr := MakeRect(
              frg.Left + (GetRectWidth(Frg) / 2) - (GetRectWidth(Frg) / 5),
              frg.Top + GetRectHeight(frectImg) / 1.23,
              GetRectWidth(Frg) / 2.5,
              GetRectHeight(Frg) / 9);
        if FValuePosition = vpBottom then
          drf := MakeRect(dr.Left,
                 frg.Top + GetRectHeight(frectImg) / 1.21,
                 GetRectWidth(dr), (GetRectHeight(Frg) / 12))
        else
          drf := MakeRect(dr.Left,
                 frectImg.Top + (GetRectHeight(frectImg) / 1.35),
                 GetRectWidth(dr), (GetRectHeight(Frg) / 12));

      AGraphics.Fill.Color := Digit.BackGroundColor;
      AGraphics.Fill.Kind := gfkSolid;
      AGraphics.Stroke.Kind := gskNone;
      AGraphics.DrawRectangle(dr);

      if UnlimitedValue then
        DisplayNumber(AGraphics, Value, drf)
      else if UnlimitedDisplayValue then
        DisplayNumber(AGraphics, DisplayValue, drf)
      else
        DisplayNumber(AGraphics, Value, drf);
    end;

    DrawString(AGraphics, fdialText, FFont, PointF(0, 0), true);
  end;

  //Pointer draw//
  for I := 0 to ExtraNeedles.Count - 1 do
    DrawPointer(AGraphics, ExtraNeedles[I].Color, ExtraNeedles[I].ShineColor, ExtraNeedles[I].ShineColorTo,
        GetCenterPoint(frectImg).X,
        GetCenterPoint(frectImg).Y, ExtraNeedles[I].Value);

  DrawPointer(AGraphics, Needle.Color, Needle.ShineColor,
        Needle.ShineColorTo,
        GetCenterPoint(frectImg).X,
        GetCenterPoint(frectImg).Y,
        fcurrentValue);

  DrawCenterPoint(AGraphics,
           frg, Round(GetCenterPoint(frg).X),
                Round(GetCenterPoint(frg).Y));

end;


procedure TTMSFNCWidgetGaugeCustom.DrawingPropertiesChanged(Sender: TObject);
begin
  Stroke.Assign(FBorder);
  Invalidate;
end;

procedure TTMSFNCWidgetGaugeCustom.SetBorder(const Value: TTMSFNCWidgetGraphicsStroke);
begin
  FBorder.Assign(Value);
  Stroke.Assign(Value);
  Invalidate;
end;

procedure TTMSFNCWidgetGaugeCustom.SetDialText(const Value: string);
begin
  if fdialText <> Value then
  begin
    fdialText := value;
    Invalidate;
  end;
end;

procedure TTMSFNCWidgetGaugeCustom.SetDigit(const Value: TTMSFNCWidgetGaugeDigit);
begin
  if FDigit <> value then
  begin
    FDigit.Assign(Value);
    Invalidate;
  end;
end;

procedure TTMSFNCWidgetGaugeCustom.SetValuePosition(
  const Value: TTMSFNCWidgetGaugeValuePosition);
begin
  if FValuePosition <> Value then
  begin
    FValuePosition := Value;
    Invalidate;
  end;
end;

procedure TTMSFNCWidgetGaugeCustom.SetDivColor(const Value: TTMSFNCGraphicsColor);
begin
  if fDivColor <> value then
  begin
    fDivColor := Value;
    Invalidate;
  end;
end;

procedure TTMSFNCWidgetGaugeCustom.SetDivWidth(const Value: integer);
begin
  if FDivWidth <> Value then
  begin
    FDivWidth := Value;
    Invalidate;
  end;
end;

procedure TTMSFNCWidgetGaugeCustom.SetExtraNeedles(const Value: TTMSFNCWidgetGaugeNeedles);
begin
  if FExtraNeedles <> Value then
  begin
    FExtraNeedles.Assign(Value);
    Invalidate;
  end;
end;

procedure TTMSFNCWidgetGaugeCustom.SetFont(const Value: TTMSFNCGraphicsFont);
begin
  FFont.AssignSource(Value);
//  if FFont <> Value then
//  begin
//    fFont.Assign(Value);
//    Invalidate;
//  end;
end;

procedure TTMSFNCWidgetGaugeCustom.SetGaugeValuesFormat(const Value: string);
begin
  if FGaugeValuesFormat <> Value then
  begin
    FGaugeValuesFormat := Value;
    Invalidate;
  end;
end;

procedure TTMSFNCWidgetGaugeCustom.SetInnerCircle(const Value: TTMSFNCWidgetGaugeInnerCircle);
begin
  if FInnerCircle <> value then
  begin
    FInnerCircle.Assign(Value);
    Invalidate;
  end;
end;

procedure TTMSFNCWidgetGaugeCustom.SetLogarithmic(const Value: Boolean);
begin
  if FLogaritchmic <> Value then
  begin
    FLogaritchmic := Value;
    if Logarithmic then
      MaximumValue := Min(LogN(LogarithmicBase, MaxDouble), MaximumValue);
    Invalidate;
  end;
end;

procedure TTMSFNCWidgetGaugeCustom.SetLogarithmicBase(const Value: Integer);
begin
  if FLogaritchmicBase <> Value then
  begin
    FLogaritchmicBase := Max(1, Value);
    if Logarithmic then
      MaximumValue := Min(LogN(LogarithmicBase, MaxDouble), MaximumValue);
    Invalidate;
  end;
end;

procedure TTMSFNCWidgetGaugeCustom.SetMaxValue(const Value: Double);
var
  v: Double;
begin
  if (value > fminValue) then
  begin
    v := Value;
    if Logarithmic then
      v := Min(LogN(LogarithmicBase, MaxDouble), v);

    fmaxValue := v;
    if Animation then
    begin
      if (FcurrentValueTo > fmaxValue) and not UnlimitedValue then
        FcurrentValueTo := fmaxValue;

      if (FcurrentDisplayValueTo > fmaxValue) and not UnlimitedDisplayValue then
        FcurrentDisplayValueTo := fmaxValue;
    end
    else
    begin
      if (FcurrentValueTo > fmaxValue) and not UnlimitedValue then
        FcurrentValueTo := fmaxValue;

      if (FcurrentDisplayValueTo > fmaxValue) and not UnlimitedDisplayValue then
        FcurrentDisplayValueTo := fmaxValue;

      FcurrentValueTo := FcurrentValue;
      FcurrentDisplayValueTo := fcurrentDisplayValue;

      DoValueChanged(Self, fcurrentValue);
      DoDisplayValueChanged(Self, fcurrentDisplayValue);
    end;

    if (Arc.Threshold.FCenter > fmaxValue) then
      Arc.Threshold.FCenter := fmaxValue;

    Invalidate;
  end;
end;

procedure TTMSFNCWidgetGaugeCustom.SetMinValue(const Value: Double);
begin
  if (value < fmaxValue) then
  begin
    fminValue := value;
    if Animation then
    begin
      if (FcurrentValueTo < fminvalue) and not UnlimitedValue then
        FcurrentValueTo := fminvalue;

      if (FcurrentDisplayValueTo < fminvalue) and not UnlimitedDisplayValue then
        FcurrentDisplayValueTo := fminValue;
    end
    else
    begin
      if (FcurrentValueTo < fminvalue) and not UnlimitedValue then
        FcurrentValueTo := fminvalue;

      if (FcurrentDisplayValueTo < fminvalue) and not UnlimitedDisplayValue then
        FcurrentDisplayValueTo := fminValue;

      FcurrentValueTo := FcurrentValue;
      fcurrentDisplayValueTo := FcurrentDisplayValue;
      DoValueChanged(Self, FcurrentValueTo);
      DoDisplayValueChanged(Self, FCurrentDisplayValueTo);
    end;

    if (Arc.Threshold.FCenter < fminValue) then
      Arc.Threshold.FCenter := fminValue;

    Invalidate;
  end;
end;

procedure TTMSFNCWidgetGaugeCustom.SetNeedle(const Value: TTMSFNCWidgetGaugeNeedle);
begin
  if FNeedle <> Value then
  begin
    FNeedle.Assign(Value);
    Invalidate;
  end;
end;

procedure TTMSFNCWidgetGaugeCustom.SetNoOfDivisions(const Value: Integer);
begin
  if ((value > 1) and (value < 25)) then
  begin
    fnoOfDivisions := value;
    Invalidate;
  end;
end;

procedure TTMSFNCWidgetGaugeCustom.SetNoOfSubDivisions(const Value: Integer);
begin
  if ((value > 0) and (value <= 10)) then
  begin
    fnoOfSubDivisions := value;
    Invalidate;
  end;
end;

procedure TTMSFNCWidgetGaugeCustom.SetOuterCircle(const Value: TTMSFNCWidgetGaugeOuterCircle);
begin
  if FOuterCircle <> value then
  begin
    FOuterCircle := Value;
    Invalidate;
  end;
end;

procedure TTMSFNCWidgetGaugeCustom.SetOuterRim(const Value: TTMSFNCWidgetGaugeOuterRim);
begin
  if FOuterRim <> value then
  begin
    FOuterRim.Assign(Value);
    Invalidate;
  end;
end;

procedure TTMSFNCWidgetGaugeCustom.SetSections(const Value: TTMSFNCWidgetGaugeSections);
begin
  if FSections <> value then
  begin
    FSections.Assign(Value);
    Invalidate;
  end;
end;

procedure TTMSFNCWidgetGaugeCustom.SetShowValues(const Value: Boolean);
begin
  if FShowValues <> Value then
  begin
    FShowValues := Value;
    Invalidate;
  end;
end;

procedure TTMSFNCWidgetGaugeCustom.SetSubDivColor(const Value: TTMSFNCGraphicsColor);
begin
  if fSubDivColor <> value then
  begin
    fSubDivColor := Value;
    Invalidate;
  end;
end;

procedure TTMSFNCWidgetGaugeCustom.SetSubDivWidth(const Value: integer);
begin
  if FSubDivWidth <> value then
  begin
    FSubDivWidth := Value;
    Invalidate;
  end;
end;

procedure TTMSFNCWidgetGaugeCustom.SetUnlimitedDisplayValue(const Value: Boolean);
begin
  if FUnlimitedDisplayValue <> Value then
  begin
    FUnlimitedDisplayValue := Value;
    Invalidate;
  end;
end;

procedure TTMSFNCWidgetGaugeCustom.SetUnlimitedValue(const Value: Boolean);
begin
  if FUnlimitedValue <> Value then
  begin
    FUnlimitedValue := Value;
    Invalidate;
  end;
end;

procedure TTMSFNCWidgetGaugeCustom.SetValue(const Value: Double);
var
  v, vl, vd: Double;
begin
  v := Value;
  vl := Value;
  vd := Value;
  if Logarithmic then
    v := LogN(LogarithmicBase, value);

  if not UnlimitedValue then
    vl := Max(Min(v, MaximumValue), MinimumValue);

  if not UnlimitedDisplayValue then
    vd := Max(Min(v, MaximumValue), MinimumValue);

  if Animation and not (csDesigning in ComponentState) and not (csLoading in ComponentState) then
  begin
    doAnimation := true;
    FAnimCurrentValue := vl;
    FcurrentValueTo := vl;
    FAnimCurrentDisplayValue := vd;
    FcurrentDisplayValueTo := vd;
  end
  else
  begin
    fcurrentValue := vl;
    FcurrentValueTo := vl;

    fcurrentDisplayValue := vd;
    FcurrentDisplayValueTo := vd;
    DoValueChanged(Self, fcurrentValue);
    DoDisplayValueChanged(Self, fcurrentDisplayValue);
  end;
  Invalidate;
end;

procedure TTMSFNCWidgetGaugeCustom.SetValueFont(const Value: TTMSFNCGraphicsFont);
begin
  if FValueFont <> Value then
  begin
    FValueFont.Assign(Value);
    Invalidate;
  end;
end;

procedure TTMSFNCWidgetGaugeCustom.SetValueFormat(const Value: string);
begin
  if FValueFormat <> Value then
  begin
    FValueFormat := Value;
    Invalidate;
  end;
end;

function TTMSFNCWidgetGaugeCustom.XYToScale(X, Y: integer): Double;
var
  K, S: Double;
  I: integer;
  pts: TTMSFNCGraphicsPathPolygon;
  pt1, pt2, pt3: TPointF;
begin
  Result := NaN;

  K := MinimumValue;
  S := (MaximumValue - MinimumValue) / (DivisionCount * (SubDivisionCount + 1));

  for I := 0 to (DivisionCount * (SubDivisionCount + 1)) do
  begin
    pts := CalcPosition(K + (S / 2));

    pt1 := pts[0];
    pt2 := PointF(GetRectWidth(frectImg) / 2, GetRectHeight(frectImg) / 2);
    pts := CalcPosition(K - (S / 2));
    pt3 := pts[0];

    SetLength(pts, 3);
    pts[0] := pt1;
    pts[1] := pt2;
    pts[2] := pt3;

    if PtInPoly(pts, X, Y) then
    begin
      if Logarithmic then
        K := Power(LogarithmicBase, K);

      Value := K;
      break;
    end;
    K := K + s;
  end;
end;

procedure TTMSFNCWidgetGaugeCustom.DrawPointer(AGraphics: TTMSFNCGraphics; col, shncol, shncolto: TTMSFNCGraphicsColor; cx: Double; cy: Double; value: Double);
var
  shpts, pts: TTMSFNCGraphicsPathPolygon;
  val, rd, a: Double;
begin
  rd := GetRectWidth(Frg) / 2 - (GetRectWidth(Frg) * 0.08);
  val := (fMaxValue - fMinValue);

  val := ((100 * (value - fMinValue)) / val);
  val := ((Arc.StopAngle - Arc.StartAngle) * val) / 100;
  val := val + Arc.StartAngle;

  a := GetRadian(val);

  SetLength(pts, 5);

  pts[0] := PointF(cx + rd * Cos(a), cy + rd * Sin(a));
  pts[4] := PointF(cx + rd * Cos(a - 0.02), cy + rd * Sin(a - 0.02));
  a := GetRadian(val +  20);
  pts[1] := PointF(cx + (GetRectWidth(Frg) * 0.09) * Cos(a), cy + (GetRectWidth(Frg) * 0.09) * Sin(a));
  pts[2] := PointF(cx, cy);
  a := GetRadian((val - 20));
  pts[3] := PointF(cx + (GetRectWidth(Frg) * 0.09) * Cos(a), cy + (GetRectWidth(Frg) * 0.09) * Sin(a));

  if col <> gcNull then
  begin
    AGraphics.Fill.Color := col;
    AGraphics.Fill.Kind := gfkSolid;
    AGraphics.Stroke.Kind := gskNone;
    AGraphics.DrawPolygon(pts);
  end;

  SetLength(shpts, 3);
  a := GetRadian(val);
  shpts[0] := PointF(cx + rd * Cos(a), cy + rd * Sin(a));

  a := GetRadian((val + 20));
  shpts[1] := PointF(cx + (GetRectWidth(Frg) * 0.09) * Cos(a), cy + (GetRectWidth(Frg) * 0.09) * Sin(a));

  shpts[2] := PointF(cx, cy);

  if (shncol <> gcNull) and (shncolto <> gcNull) then
  begin
    AGraphics.Fill.Color := shncol;
    AGraphics.Fill.ColorTo := shncolto;
    AGraphics.Fill.Kind := gfkGradient;
    AGraphics.DrawPolygon(shpts);
  end;
end;

procedure TTMSFNCWidgetGaugeCustom.DrawCenterPoint(AGraphics: TTMSFNCGraphics; rect: TRectF; cX: Integer;
  cY: Integer);
var
  r: TRectF;
  sht: Double;
begin
  if (Needle.OuterCenterColor <> gcNull) and (Needle.OuterCenterColorTo <> gcNull) then
  begin
    sht := (GetRectWidth(frectImg) / 5);
    r := MakeRect((cX - (sht / 2)), (cY - (sht / 2)), sht, sht);
    AGraphics.Fill.Color := Needle.OuterCenterColor;
    AGraphics.Fill.ColorTo := Needle.OuterCenterColorTo;
    AGraphics.Fill.Kind := gfkGradient;
    AGraphics.Stroke.Kind := gskNone;
    AGraphics.DrawEllipse(r);
  end;

  if (Needle.InnerCenterColor <> gcNull) and (Needle.InnerCenterColorTo <> gcNull) then
  begin
    sht := (GetRectWidth(frectImg) / 7);
    r := MakeRect((cX - (sht / 2)), (cY - (sht / 2)), sht, sht);
    AGraphics.Fill.Color := Needle.InnerCenterColor;
    AGraphics.Fill.ColorTo := Needle.InnerCenterColorTo;
    AGraphics.Fill.Kind := gfkGradient;
    AGraphics.Stroke.Kind := gskNone;
    AGraphics.DrawEllipse(r);
  end;
end;

procedure TTMSFNCWidgetGaugeCustom.DrawSections(AGraphics: TTMSFNCGraphics);
var
  i: integer;
  sa, swa: Double;
  path: TTMSFNCGraphicsPath;
  r, rin: TRectF;
  rlen: Single;
  //colors:array[0..2] of TTMSFNCGraphicsColor;
  //positions: array[0..2] of single;
  innermg, outermg: Double;
  gp: Integer;
  ca, y1, y, rd: Double;
  rpos: TRectF;
  cy: Double;
  rect: TRectF;
begin
  for I := 0 to Sections.Count - 1 do
  begin
    with Sections[I] do
    begin
      if Color <> gcNull then
      begin
        innermg := InnerMargin;
        outermg := OuterMargin;

        rect := frectImg;
        cy := Round(GetRectHeight(frectImg) / 2);
        ca := GetRadian(270);
        gp := Round(GetRectWidth(frectImg) * 0.01);
        rpos := MakeRect((rect.Left + gp), (rect.Top + gp), (GetRectWidth(rect) - gp), (GetRectHeight(rect) - gp));
        rd := ((GetRectWidth(rpos) / 2) - (gp * 5));
        y := (cY + rd * Sin(ca));
        y1 := (cY + (rd - GetRectWidth(frectImg) / 20) * Sin(ca));

        if SectionType = stBorder then
        begin
          innermg := (GetRectHeight(frectImg) / 2) - y1;
          outermg := y;
        end;

        sa := GetAngle(StartValue);
        swa := GetAngle(EndValue) - sa;
        path := TTMSFNCGraphicsPath.Create;
        r := MakeRect(frectImg.Left + outermg, frectImg.Top + outermg, GetRectWidth(frectImg) - (outermg * 2), GetRectHeight(frectImg) - (outermg * 2));
        rlen := Min(GetRectWidth(r)/2, GetRectHeight(r)/2);
        rin := MakeRect(frectImg.Left +(GetRectWidth(frectImg) / 2) - innermg,
                        frectImg.Top + (GetRectHeight(frectImg) / 2) - innermg, innermg * 2, innermg * 2);
        if innermg > 0 then
        begin
          path.AddArc(GetCenterPoint(r), PointF(rlen, rlen), sa + swa, -swa);
          rlen := Min(GetRectWidth(rin)/2, GetRectHeight(rin)/2);
          path.AddArc(GetCenterPoint(rin), PointF(rlen, rlen), sa, swa);
        end
        else
        begin
          //Equicalent of AddPie. Last closing line is automatic.
          path.AddArc(GetCenterPoint(r), PointF(rlen, rlen), sa, swa);
          path.LineTo(GetCenterPoint(r));
        end;

        Path.ClosePath;
        AGraphics.Stroke.Color := Color;
        AGraphics.Fill.Color := Color;
        AGraphics.Fill.Kind := gfkSolid;
        AGraphics.DrawPath(path);
        path.Free;
      end;
    end;
  end;
end;

procedure TTMSFNCWidgetGaugeCustom.DrawString(AGraphics: TTMSFNCGraphics; value: string; ft: TTMSFNCGraphicsFont; pt: TPointF; DialDraw: Boolean);
var
  sizeRect: TRectF;
  digiFRectText: TRectF;
begin
  if DialDraw then
  begin
    AGraphics.Font.Assign(ft);
    sizeRect := AGraphics.CalculateText(value);
    if FValuePosition = vpBottom then
      digiFRectText := MakeRect(frectImg.Left + (GetRectWidth(frectImg) / 2) - (GetRectWidth(sizeRect) / 2),
        frectImg.Top + (GetRectHeight(frectImg) / 1.5),
        GetRectWidth(sizeRect), GetRectHeight(sizeRect))
    else
      digiFRectText := MakeRect(frectImg.Left + (GetRectWidth(frectImg) / 2) - (GetRectWidth(sizeRect) / 2),
        frg.Top + GetRectHeight(frectImg) / 1.21,
        GetRectWidth(sizeRect), GetRectHeight(sizeRect));
    AGraphics.DrawText(digiFRectText, value,
            false, gtaCenter, gtaCenter, gttNone, 0, -1, -1, true);
  end
  else
  begin
    AGraphics.Font.Assign(ft);
    FNCUtilDrawTextOnPoint(value, AGraphics, pt, gtaCenter, 0, true);
  end;
end;

procedure TTMSFNCWidgetGaugeCustom.DrawCalibration(AGraphics: TTMSFNCGraphics);
var
  i: Integer;
  str: String;
  val: Double;
begin
  AGraphics.Fill.Kind := gfkNone;

  for I := 0 to Length(FCalibrationValues) - 1 do
  begin
    with FCalibrationValues[i] do
    begin
      case ValueType of
        cvSmall:
        begin
          if SubDivisionColor <> gcNull then
          begin
            AGraphics.Stroke.Color := SubDivisionColor;
            AGraphics.Stroke.Width := SubDivisionWidth;
            AGraphics.DrawLine(PointF(x, y), PointF(x1, y1));
          end;
        end;
        cvLarge:
        begin
          if DivisionColor <> gcNull then
          begin
            AGraphics.Stroke.Color := DivisionColor;
            AGraphics.Stroke.Width := DivisionWidth;
            AGraphics.DrawLine(PointF(x, y),
                 PointF(x1, y1));
          end;

          if ShowValues then
          begin
            val := rv;
            if Logarithmic then
              val := Power(LogarithmicBase, val);

//            str := floattostr(val);
            str := FormatFloat(GaugeValuesFormat, val);
            if Assigned(OnGetValueText) then
              OnGetValueText(Self, val, str);

            DrawString(AGraphics, str, ValueFont, PointF(tx, ty), false);
          end;
        end;
      end;
    end;
  end;
end;

function TTMSFNCWidgetGaugeCustom.GetAngle(Value: Double): Double;
begin
  result := ((Arc.StopAngle - Arc.StartAngle) / (MaximumValue - MinimumValue)) * (Value - MinimumValue);
  Result := Result + Arc.StartAngle;
end;

function TTMSFNCWidgetGaugeCustom.GetDisplayValue: Double;
begin
  Result := fcurrentDisplayValue;
  if Logarithmic then
    Result := Power(LogarithmicBase, Result);
end;

function TTMSFNCWidgetGaugeCustom.GetDocURL: string;
begin
  Result := TTMSFNCDashboardDocURL + '/components/ttmsfncwidgetgauge/';
end;

function TTMSFNCWidgetGaugeCustom.GetGaugeValueTextSize(AValue: Double; AWidth: Boolean): Integer;
var
  g: TTMSFNCGraphics;
  ts: TSizeF;
  val: Double;
begin
  g := TTMSFNCGraphics.CreateBitmapCanvas;
  g.BeginScene;
  try
    g.Font.Assign(ValueFont);
    val := AValue;
    if Logarithmic then
      val := Power(LogarithmicBase, val);

    ts := g.CalculateTextSize(FormatFloat(GaugeValuesFormat, val), RectF(0, 0, 10000, 10000), True, True);
    if AWidth then
      Result := Round(ts.cx)
    else
      Result := Round(ts.cy);
  finally
    g.EndScene;
    g.Free;
  end;
  //
end;

function TTMSFNCWidgetGaugeCustom.GetRadian(theta: Double): Double;
begin
  Result := theta * (PI / 180);
end;

function TTMSFNCWidgetGaugeCustom.GetTipsURL: string;
begin
  Result := TTMSFNCDashboardTipsURL;
end;

function TTMSFNCWidgetGaugeCustom.GetValue: Double;
begin
  Result := fcurrentValue;
  if Logarithmic then
    Result := Power(LogarithmicBase, Result);
end;

procedure TTMSFNCWidgetGaugeCustom.DisplayNumber(AGraphics: TTMSFNCGraphics; number: Double; drect: TRectF);
var
  num: String;
  i: Integer;
  drawDPS, drawTS: Boolean;
  shift: Double;
  numch: char;
begin
  if Assigned(OnDisplayValue) then
    FOnDisplayValue(Self, number);

  num := FormatFloat(ValueFormat, number);
  shift := 0;
  for i := 1 to length(num) do
  begin
    numch := num[i];
    case numch of
    '.',',': shift := shift + 2 * (GetRectWidth(frectImg) / 250);
    '-': shift := shift + 15 * (GetRectWidth(frectImg) / 250);
    '0'..'9': shift := shift + 15 * (GetRectWidth(frectImg) / 250);
    end;
  end;

  case Digit.Alignment of
    taLeftJustify: shift := 0;
    taRightJustify: shift := (GetRectWidth(drect) - shift);
    taCenter: shift := (GetRectWidth(drect) - shift) / 2;
  end;

  {$IFDEF ZEROSTRINGINDEX}
  for i := 0 to length(num) - 1 do
  {$ELSE}
  for i := 1 to length(num) do
  {$ENDIF}
  begin
    numch := num[i];
    drawDPS := (i < length(num) - 1) and (num[i+1] = '.');
    drawTS := (i < length(num) - 1) and (num[i+1] = ',');
    case numch of
    '.',',': shift := shift + 2 * (GetRectWidth(frectImg) / 250);
    '-':
      begin
        DrawDigit(AGraphics, -1, PointF((drect.Left + shift), drect.Top), drawDPS, drawTS, Round(GetRectHeight(drect)));
        shift := shift + 15 * (GetRectWidth(frectImg) / 250);
      end;
    '0'..'9':
      begin
        DrawDigit(AGraphics, ord(numch) - ord('0') , PointF((drect.Left + shift), drect.Top), drawDPS, drawTS, Round(GetRectHeight(drect)));
        shift := shift + 15 * (GetRectWidth(frectImg) / 250);
      end;
    end;
  end;
end;

procedure TTMSFNCWidgetGaugeCustom.DoDisplayValueChanged(Sender: TObject; value: Double);
begin
  if Logarithmic then
    value := Power(LogarithmicBase, value);

  if Assigned(OnDisplayValueChanged) then
    OnDisplayValueChanged(Sender, value);
end;

procedure TTMSFNCWidgetGaugeCustom.DoValueChanged(Sender: TObject; value: Double);
begin
  if Logarithmic then
    value := Power(LogarithmicBase, value);
  if Assigned(OnValueChanged) then
    OnValueChanged(Sender, value);
end;

procedure TTMSFNCWidgetGaugeCustom.DrawDigit(AGraphics: TTMSFNCGraphics; number: Integer; position: TPointF; dp, t: Boolean; h: integer);
var
  segmentG: TTMSFNCGraphicsPathPolygon;
  segmentF: TTMSFNCGraphicsPathPolygon;
  segmentE: TTMSFNCGraphicsPathPolygon;
  segmentD: TTMSFNCGraphicsPathPolygon;
  segmentC: TTMSFNCGraphicsPathPolygon;
  segmentB: TTMSFNCGraphicsPathPolygon;
  segmentA: TTMSFNCGraphicsPathPolygon;
  w: Double;
begin
  w := 10 * (h / 13);

  if Digit.Color <> gcNull then
  begin
    SetLength(segmentA, 5);
    segmentA[0] := PointF((position.X + GetX(2.8, w)), (position.Y + GetY(1, h)));
    segmentA[1] := PointF((position.X + GetX(10, w)), (position.Y + GetY(1, h)));
    segmentA[2] := PointF((position.X + GetX(8.8, w)), (position.Y + GetY(2, h)));
    segmentA[3] := PointF((position.X + GetX(3.8, w)), (position.Y + GetY(2, h)));
    segmentA[4] := segmentA[0];

    SetLength(segmentB, 5);
    segmentB[0] := PointF((position.X + GetX(10, w)), (position.Y + GetY(1.4, h)));
    segmentB[1] := PointF((position.X + GetX(9.3, w)), (position.Y + GetY(6.8, h)));
    segmentB[2] := PointF((position.X + GetX(8.4, w)), (position.Y + GetY(6.4, h)));
    segmentB[3] := PointF((position.X + GetX(9, w)), (position.Y + GetY(2.2, h)));
    segmentB[4] := segmentB[0];

    SetLength(segmentC, 5);
    segmentC[0] := PointF((position.X + GetX(9.2, w)), (position.Y + GetY(7.2, h)));
    segmentC[1] := PointF((position.X + GetX(8.7, w)), (position.Y + GetY(12.7, h)));
    segmentC[2] := PointF((position.X + GetX(7.6, w)), (position.Y + GetY(11.9, h)));
    segmentC[3] := PointF((position.X + GetX(8.2, w)), (position.Y + GetY(7.7, h)));
    segmentC[4] := segmentC[0];

    SetLength(segmentD, 5);
    segmentD[0] := PointF((position.X + GetX(7.4, w)), (position.Y + GetY(12.1, h)));
    segmentD[1] := PointF((position.X + GetX(8.4, w)), (position.Y + GetY(13, h)));
    segmentD[2] := PointF((position.X + GetX(1.3, w)), (position.Y + GetY(13, h)));
    segmentD[3] := PointF((position.X + GetX(2.2, w)), (position.Y + GetY(12.1, h)));
    segmentD[4] := segmentD[0];

    SetLength(segmentE, 5);
    segmentE[0] := PointF((position.X + GetX(2.2, w)), (position.Y + GetY(11.8, h)));
    segmentE[1] := PointF((position.X + GetX(1, w)), (position.Y + GetY(12.7, h)));
    segmentE[2] := PointF((position.X + GetX(1.7, w)), (position.Y + GetY(7.2, h)));
    segmentE[3] := PointF((position.X + GetX(2.8, w)), (position.Y + GetY(7.7, h)));
    segmentE[4] := segmentE[0];

    SetLength(segmentF, 5);
    segmentF[0] := PointF((position.X + GetX(3, w)), (position.Y + GetY(6.4, h)));
    segmentF[1] := PointF((position.X + GetX(1.8, w)), (position.Y + GetY(6.8, h)));
    segmentF[2] := PointF((position.X + GetX(2.6, w)), (position.Y + GetY(1.3, h)));
    segmentF[3] := PointF((position.X + GetX(3.6, w)), (position.Y + GetY(2.2, h)));
    segmentF[4] := segmentF[0];

    SetLength(segmentG, 7);
    segmentG[0] := PointF((position.X + GetX(2, w)), (position.Y + GetY(7, h)));
    segmentG[1] := PointF((position.X + GetX(3.1, w)), (position.Y + GetY(6.5, h)));
    segmentG[2] := PointF((position.X + GetX(8.3, w)), (position.Y + GetY(6.5, h)));
    segmentG[3] := PointF((position.X + GetX(9, w)), (position.Y + GetY(7, h)));
    segmentG[4] := PointF((position.X + GetX(8.2, w)), (position.Y + GetY(7.5, h)));
    segmentG[5] := PointF((position.X + GetX(2.9, w)), (position.Y + GetY(7.5, h)));
    segmentG[6] := segmentG[0];

    AGraphics.Stroke.Kind := gskNone;
    AGraphics.Fill.Kind := gfkSolid;
    AGraphics.Fill.Color := InnerCircle.Color; //equivalent to off

    AGraphics.DrawPolygon(segmentA);
    AGraphics.DrawPolygon(segmentB);
    AGraphics.DrawPolygon(segmentC);
    AGraphics.DrawPolygon(segmentD);
    AGraphics.DrawPolygon(segmentE);
    AGraphics.DrawPolygon(segmentF);
    AGraphics.DrawPolygon(segmentG);

    AGraphics.Fill.Color := Digit.Color;

    if IsNumberAvailable(number, [0, 2, 3, 5, 6, 7, 8, 9]) then
      AGraphics.DrawPolygon(SegmentA);
    if IsNumberAvailable(number, [0, 1, 2, 3, 4, 7, 8, 9]) then
      AGraphics.DrawPolygon(segmentB);
    if IsNumberAvailable(number, [0, 1, 3, 4, 5, 6, 7, 8, 9]) then
      AGraphics.DrawPolygon(segmentC);
    if IsNumberAvailable(number, [0, 2, 3, 5, 6, 8, 9]) then
      AGraphics.DrawPolygon(segmentD);
    if IsNumberAvailable(number, [0, 2, 6, 8]) then
      AGraphics.DrawPolygon(segmentE);
    if IsNumberAvailable(number, [0, 4, 5, 6, 7, 8, 9]) then
      AGraphics.DrawPolygon(segmentF);
    if IsNumberAvailable(number, [2, 3, 4, 5, 6, 8, 9, -1]) then
      AGraphics.DrawPolygon(segmentG);
    if dp then
      AGraphics.DrawEllipse((position.X + GetX(10, w)),
                            (position.Y + GetY(12, h)),
                            (position.X + GetX(10, w)) + (w / 7),
                            (position.Y + GetY(12, h)) + (w / 7)
                            );
    if t then
    begin
      AGraphics.DrawRectangle((position.X + GetX(10, w)),
                            (position.Y + GetY(11, h)),
                            (position.X + GetX(10, w)) + (w / 7),
                            (position.Y + GetY(11, h)) + (w / 3)
                            );
    end;
  end;
end;

function TTMSFNCWidgetGaugeCustom.GetX(x: Double; awidth: Double): Double;
begin
  Result := x * awidth / 12;
end;

function TTMSFNCWidgetGaugeCustom.GetY(y: Double; aheight: Double): Double;
begin
  Result := y * aheight / 15;
end;

function TTMSFNCWidgetGaugeCustom.IsNumberAvailable(Number: integer; const listOfNumbers: array of Integer): Boolean;
var
  i: Integer;
begin
  Result := False;
  if (Length(listOfNumbers) > 0) then
  begin
    for i := 0 to Length(ListOfNumbers) - 1 do
    begin
      if (ListOfNumbers[i] = number) then
      begin
        Result := True;
        Exit;
      end;
    end;
  end;
end;

procedure TTMSFNCWidgetGaugeCustom.Loaded;
begin
  inherited;
end;

procedure TTMSFNCWidgetGaugeCustom.SetAnimation(const Value: Boolean);
begin
  if FAnimation <> value then
  begin
    FAnimation := Value;
    if not FAnimation then
    begin
      FCurrentValue := FAnimCurrentValue;
      FCurrentDisplayValue := FAnimCurrentDisplayValue;
    end;
    Invalidate;
  end;
end;

procedure TTMSFNCWidgetGaugeCustom.SetAnimationFactor(const Value: integer);
begin
  if FAnimationFactor <> value then
  begin
    FAnimationFactor := Value;
    Invalidate;
  end;
end;

procedure TTMSFNCWidgetGaugeCustom.SetArc(const Value: TTMSFNCWidgetGaugeArc);
begin
  if Assigned(FArc) then
  begin
    FArc.Assign(Value);
    Invalidate;
  end;
end;

procedure TTMSFNCWidgetGaugeCustom.SetCircleEndValue(const Value: Integer);
begin
  if (FCircleEndValue <> value) and (Value <= 360) and (Value >= CircleStartValue) then
  begin
    FCircleEndValue := Value;
    Invalidate;
  end;
end;

procedure TTMSFNCWidgetGaugeCustom.SetMarginOuter(const Value: Integer);
begin
  if (FMarginOuter <> value) then
  begin
    FMarginOuter := Value;
    Invalidate;
  end;
end;


procedure TTMSFNCWidgetGaugeCustom.SetCircleStartValue(const Value: Integer);
begin
  if (FCircleStartValue <> value) and (Value >= 0) and (Value <= CircleEndValue) then
  begin
    FCircleStartValue := Value;
    Invalidate;
  end;
end;

function TTMSFNCWidgetGaugeCustom.CalcPosition(Value: Double): TTMSFNCGraphicsPathPolygon;
var
  pts: TTMSFNCGraphicsPathPolygon;
  val, rd, a, cx, cy: Double;
begin
  rd := GetRectWidth(frectImg) / 2 - (GetRectWidth(frectImg) * 0.12);
  val := (fMaxValue - fMinValue);
  val := ((100 * (Value - fMinValue)) / val);
  val := ((Arc.StopAngle - Arc.StartAngle) * val) / 100;
  val := val + Arc.StartAngle;

  a := GetRadian(val);

  SetLength(pts, 5);

  cx := GetCenterPoint(frectImg).X;
  cy := GetCenterPoint(frectImg).Y;

  pts[0] := PointF(cx + rd * Cos(a), cy + rd * Sin(a));
  pts[4] := PointF(cx + rd * Cos(a - 0.02), cy + rd * Sin(a - 0.02));
  a := GetRadian(val +  20);
  pts[1] := PointF(cx + (GetRectWidth(frectImg) * 0.09) * Cos(a), cy + (GetRectWidth(frectImg) * 0.09) * Sin(a));
  pts[2] := PointF(cx, cy);
  a := GetRadian((val - 20));
  pts[3] := PointF(cx + (GetRectWidth(frectImg) * 0.09) * Cos(a), cy + (GetRectWidth(frectImg) * 0.09) * Sin(a));

  result := pts;
end;

function TTMSFNCWidgetGaugeCustom.PtInPoly(const Points: TTMSFNCGraphicsPathPolygon; X,
  Y: Integer): Boolean;
var
  Count, K, J : Integer;
begin
  Result := False;
  Count := Length(Points);
  J := Count-1;
  for K := 0 to Count-1 do begin
   if ((Points[K].Y <=Y) and (Y < Points[J].Y)) or
      ((Points[J].Y <=Y) and (Y < Points[K].Y)) then
   begin
    if (x < (Points[j].X - Points[K].X) *
       (y - Points[K].Y) /
       (Points[j].Y - Points[K].Y) + Points[K].X) then
        Result := not Result;
    end;
    J := K;
  end;
end;

procedure TTMSFNCWidgetGaugeCustom.CalculateArcThresHold;
var
  val: Double;
  gp: integer;
begin
  gp := Round(GetRectWidth(frectImg) * Arc.Width);
  Frg := Makerect((frectImg.Left + gp), (frectImg.Top + gp), (GetRectWidth(frectImg) - (gp * 2)), (GetRectHeight(frectImg) - (gp * 2)));

  Frg := MakeRect((frectImg.Left + gp), (frectImg.Top + gp),
      (GetRectWidth(frectImg) - (gp * 2)), (GetRectHeight(frectImg) - (gp * 2)));

  val := (fMaxValue - fMinValue);
  val := ((100 * (Arc.Threshold.Center - fMinValue)) / val);
  val := ((Arc.StopAngle - Arc.StartAngle) * val) / 100;
  val := val + Arc.StartAngle;

  Fsta := (val - ((Arc.Threshold.SweepAngle * Arc.Threshold.Span) / 200));

  if (Fsta <= Arc.StartAngle) then
    Fsta := Arc.StartAngle;

  Fswpa := ((Arc.Threshold.SweepAngle * Arc.Threshold.Span) / 100);

  if ((Fsta + Fswpa) > Arc.StopAngle) then
    Fswpa := (Arc.StopAngle - Fsta);
end;

procedure TTMSFNCWidgetGaugeCustom.CalculateCalibration;
var
  i, j, p, k, gp: Integer;
  rv, ca, sht, incr, ta, rd, tx, ty, y1, x1, y, x: Double;
  r: TRectF;
  noint: Integer;
  nopart: Integer;
  rect: TRectF;
  cx, cy: Double;
begin
  for I := 0 to Length(FCalibrationValues) - 1 do
    FCalibrationValues[I].ValueType := cvNone;

  rect := frectImg;
  cx := GetCenterPoint(rect).X;
  cy := GetCenterPoint(rect).Y;
  nopart := (fnoOfDivisions + 1);
  noint := fnoOfSubDivisions;
  ca := GetRadian(Arc.StartAngle);
  gp := Round(GetRectWidth(rect) * 0.01);
  sht := (GetRectWidth(rect) / 30);
  r := MakeRect((rect.Left + gp), (rect.Top + gp), (GetRectWidth(rect) - gp), (GetRectHeight(rect) - gp));
  rd := ((GetRectWidth(r) / 2) - (gp * 5));
  ta := (Arc.StopAngle - Arc.StartAngle);
  incr := GetRadian(((ta) / ((nopart - 1) * (noint + 1))));
  rv := MinimumValue;
  i := 0;
  p := 0;
  while (i <= nopart) do
  begin
    x := (cX + rd * Cos(ca));
    y := (cY + rd * Sin(ca));
    x1 := (cX + (rd - GetRectWidth(r) / 20) * Cos(ca));
    y1 := (cY + (rd - GetRectWidth(r) / 20) * Sin(ca));
    FCalibrationValues[p].x := x;
    FCalibrationValues[p].y := y;
    FCalibrationValues[p].x1 := x1;
    FCalibrationValues[p].y1 := y1;
    tx := (cX + (rd - GetGaugeValueTextSize(rv, True) / 2 - GetRectWidth(r) / 12) * Cos(ca));
//    tx := cX + (rd - GetRectWidth(r)/ 9) * Cos(ca);
    ty := (cY - sht + (rd - GetGaugeValueTextSize(rv, False) / 2 - GetRectWidth(r) / 12) * Sin(ca));
//    ty := cY - sht + (rd - GetRectWidth(r) / 9) * Sin(ca);
    FCalibrationValues[p].tx := tx;
    FCalibrationValues[p].ty := ty;
    FCalibrationValues[p].ValueType := cvLarge;
    FCalibrationValues[p].rv := rv;
    Inc(p);
    rv := rv * 10;
    rv := rv + ((MaximumValue - MinimumValue) / (nopart - 1)) * 10;
    rv := rv / 10;
    if (i = (nopart - 1)) then
      break;
    j := 0;
    while (j <= noint) do
    begin
      ca := ca + incr;
      x := cX + rd * Cos(ca);
      y := cY + rd * Sin(ca);
      x1 := cX + (rd - GetRectWidth(r)/50) * Cos(ca);
      y1 := cY + (rd - GetRectWidth(r)/50) * Sin(ca);
      k := p;
      FCalibrationValues[k].x := x;
      FCalibrationValues[k].y := y;
      FCalibrationValues[k].x1 := x1;
      FCalibrationValues[k].y1 := y1;
      FCalibrationValues[k].ValueType := cvSmall;
      Inc(p);
      Inc(J);
    end;
    Inc(I);
  end;
end;

function AnimateDouble(var Start, Stop: Double; Delta: Double; Margin: Double): Boolean;
begin
  Result := true;
  if (Start > Stop - Margin) and (Start < Stop + Margin) then
  begin
    Start := Stop;
    Result := false;
  end
  else
  begin
    Delta := Max(Margin, Delta);
    if Start < Stop then
      Start := Start + Delta
    else
      Start := Start - Delta;
  end;
end;

procedure TTMSFNCWidgetGaugeCustom.AnimateGauge(Sender: TObject);
var
  d, pos: Double;
  res: Boolean;
  doanim, doanimdis: Boolean;
begin
  if Animation and not (csLoading in ComponentState) and not (csDesigning in ComponentState) and doAnimation then
  begin
    d := Abs(FcurrentValueTo - fcurrentValue) / AnimationFactor;
    pos := fcurrentValue;
    res := AnimateDouble(pos, FcurrentValueTo, d, 0.001);
    if res then
    begin
      doanim := True;
      fcurrentValue := pos;
      DoValueChanged(Self, pos);
      Invalidate;
    end
    else
    begin
      doanim := false;
      FcurrentValue := FcurrentValueTo;
      Invalidate;
    end;

    d := Abs(FcurrentDisplayValueTo - fcurrentDisplayValue) / AnimationFactor;
    pos := fcurrentDisplayValue;
    res := AnimateDouble(pos, FcurrentDisplayValueTo, d, 0.001);
    if res then
    begin
      doanimdis := True;
      fcurrentDisplayValue := pos;
      DoDisplayValueChanged(Self, pos);
      Invalidate;
    end
    else
    begin
      doanimdis := False;
      FcurrentDisplayValue := FcurrentDisplayValueTo;
      Invalidate;
    end;

    doanimation := doanimdis or doanim;
  end;
end;

{TTMSFNCWidgetGauge}

constructor TTMSFNCWidgetGauge.Create(AOwner: TComponent);
begin
  inherited;
end;

destructor TTMSFNCWidgetGauge.Destroy;
begin
  inherited;
end;

procedure TTMSFNCWidgetGauge.Assign(Source: TPersistent);
begin
  inherited Assign(Source);
end;

function TTMSFNCWidgetGauge.GetVersion: string;
begin
  Result := GetVersionNumber(MAJ_VER, MIN_VER, REL_VER, BLD_VER);
end;

procedure TTMSFNCWidgetGauge.RegisterRuntimeClasses;
begin
  inherited;
  RegisterClasses([TTMSFNCWidgetGaugeSection]);
end;

end.
